import React, {useEffect, useState, useMemo} from 'react'
import {
  AlertTriangle,
  AlignLeft,
  Anchor,
  ArrowRight,
  BadgePercent,
  Building2,
  Calendar,
  CalendarClock,
  Check,
  CheckCircle2,
  ClipboardList,
  Coins,
  Copy,
  CreditCard,
  Download,
  ExternalLink,
  Eye,
  FileCheck2,
  FileDigit,
  FileSpreadsheet,
  FileText,
  GitBranch,
  Hash,
  Info,
  Layers,
  Loader,
  MapPin,
  PackageCheck,
  Paperclip,
  Receipt,
  Route,
  Scale,
  Search,
  Send,
  ShieldCheck,
  Ship,
  ShoppingCart,
  Tag,
  Timer,
  Truck,
  User,
  UserCheck,
  UserX,
  X,
  XCircle,
} from 'lucide-react'
import axios from 'axios'
import {
  getVesselInvoiceJourney,
  getVesselInvoices,
  updateVesselInvoiceStatus,
  getGoodsReceiptById,
  getPurchaseOrderById,
  getQuotationById,
  getRfqById,
  getRequisitionById,
} from '../services/vesselInvoiceService'
import type {InvoiceJourneyData, VesselInvoiceApi, InvoiceLineItem} from '../types/invoice'
import {setupAxios, useAuth} from '../../auth'
import {ACCOUNTS_PERMS} from '../core/permissions'
import {useGlobalFilter} from '../../../shared/context/GlobalFilterContext'
import {downloadAttachmentBlob} from '../../Procurement/core/attachmentUtils'

const api = axios.create({baseURL: process.env.REACT_APP_ACCOUNTS_API_URL || ''})
setupAxios(api)

const procurementApi = axios.create({baseURL: process.env.REACT_APP_API_URL || '/api'})
setupAxios(procurementApi)

type VesselInvoiceStatus = 'Draft' | 'Submitted' | 'Approved' | 'Paid' | 'Rejected'

type ParsedLineItem = {
  name: string
  quantity: number
  uom: string
  unitPrice: string
  discountPercent?: number
  taxPercent: number
  total: string
  accountCode?: string
}

type VesselExpenseInvoice = {
  id: number
  invoiceNumber: string
  vesselName: string
  vesselImo?: string
  vesselType?: string
  vesselFlag?: string
  expenseType: string
  date: string
  amount: string
  rawAmount: number
  paidAmount?: string
  status: VesselInvoiceStatus
  threeWayMatched: boolean
  threeWayMatchDetails?: string
  approvalRemarks?: string
  details: {
    vendor: string
    vendorCompanyName?: string
    vendorContact?: string
    vendorEmail?: string
    vendorPhone?: string
    vendorPan?: string
    vendorGst?: string
    vendorAddress?: string
    description: string
    currency: string
    port: string
    paymentTerms: string
  }
  poDetails?: {
    poNumber?: string
    poDate?: string
    poStatus?: string
    poAmount?: string
    isPartialBilling?: boolean
    rawPoAmount?: number
    bargeCost?: string
    freightCost?: string
    truckCost?: string
    packagingCost?: string
    otherCost?: string
    totalDiscount?: string
    budgetCode?: string
    glAccountCode?: string
    glSubAccountCode?: string
    accountCategory?: string
    subtotal?: string
    taxAmount?: string
    grandTotal?: string
  }
  reqDetails?: {
    reqCode?: string
    reqTitle?: string
    urgency?: string
    justification?: string
  }
  rfqCode?: string
  grnDetails?: {
    grnId?: number
    receivedDate?: string
    status?: string
    discrepancy?: string
    receivedQty?: number
    generalNotes?: string
  }
  lineItems: ParsedLineItem[]
  approvedBy?: string
  approvedAt?: string
  rejectedBy?: string
  rejectedAt?: string
  transactionId?: string
  attachmentUrl?: string
  paymentTerms?: string
  purchaseOrderId?: number | null
  goodsReceiptId?: number | null
  vendorId?: number | null
  submittedAt?: string | null
  rawInvoice?: any
}

type ApprovalModalData = {id: number}
type RejectModalData = {id: number}

const FALLBACK_CURRENCY = 'USD'

const resolveAttachmentUrl = (path?: string | null) => {
  if (!path) return null
  if (/^https?:\/\//i.test(path)) return path
  const base = api.defaults.baseURL ?? ''
  if (!base) return path
  const baseWithoutApi = base.replace(/\/?api\/?$/, '')
  const normalizedBase = baseWithoutApi.endsWith('/') ? baseWithoutApi.slice(0, -1) : baseWithoutApi
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path
  return `${normalizedBase}/${normalizedPath}`
}

const formatMoney = (amount: number | string | null | undefined, currency = 'USD') => {
  if (amount == null || amount === '') return '$0.00'
  const numeric = typeof amount === 'number' ? amount : parseFloat(amount)
  if (isNaN(numeric)) return '$0.00'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.length === 3 ? currency : 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric)
}

const formatDate = (dateStr?: string | null) => {
  if (!dateStr) return '—'
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return dateStr
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return dateStr
  }
}

const toUiStatus = (invoice: VesselInvoiceApi): VesselInvoiceStatus => {
  const rawStatus = (invoice.status || invoice.approval_status || '').toUpperCase()
  if (rawStatus === 'PAID') return 'Paid'
  if (rawStatus === 'REJECTED' || invoice.rejected_at || invoice.rejected_by) return 'Rejected'
  if (
    rawStatus === 'APPROVED' ||
    invoice.approved_at ||
    invoice.approved_by ||
    Boolean(invoice.is_approved) ||
    (invoice.is_approved as any) === 1
  ) {
    return 'Approved'
  }
  if (rawStatus === 'SUBMITTED' || invoice.submitted_at || invoice.submitted_by) return 'Submitted'
  if (rawStatus === 'DRAFT') return 'Draft'
  return 'Draft'
}

const getStatusBadgeConfig = (status: VesselInvoiceStatus) => {
  switch (status) {
    case 'Approved':
      return {
        label: 'Approved',
        className: 'bg-emerald-100 text-emerald-900 border-2 border-emerald-500 ring-2 ring-emerald-500/20 font-black tracking-wide shadow-sm',
        dotColor: 'bg-emerald-600',
        pulse: true,
        icon: CheckCircle2,
      }
    case 'Paid':
      return {
        label: 'Paid',
        className: 'bg-teal-100 text-teal-950 border-2 border-teal-500 ring-2 ring-teal-500/20 font-black tracking-wide shadow-sm',
        dotColor: 'bg-teal-600',
        pulse: false,
        icon: CreditCard,
      }
    case 'Submitted':
      return {
        label: 'Submitted',
        className: 'bg-sky-50 text-sky-800 border border-sky-300 font-bold',
        dotColor: 'bg-sky-500',
        pulse: false,
        icon: Send,
      }
    case 'Draft':
      return {
        label: 'Draft',
        className: 'bg-slate-100 text-slate-700 border border-slate-300 font-semibold',
        dotColor: 'bg-slate-400',
        pulse: false,
        icon: FileText,
      }
    case 'Rejected':
      return {
        label: 'Rejected',
        className: 'bg-red-100 text-red-800 border-2 border-red-400 font-bold',
        dotColor: 'bg-red-500',
        pulse: false,
        icon: XCircle,
      }
    default:
      return {
        label: status,
        className: 'bg-slate-100 text-slate-700 border border-slate-300 font-semibold',
        dotColor: 'bg-slate-400',
        pulse: false,
        icon: FileText,
      }
  }
}

const mapInvoiceToUi = (invoice: VesselInvoiceApi): VesselExpenseInvoice => {
  const po = invoice.purchaseOrder
  const quotation = po?.quotation
  const rfq = quotation?.rfq
  const requisition = rfq?.requisition
  const nestedVessel = requisition?.vessel

  const vendorInfo = invoice.vendor_info
  const vendorLabel =
    vendorInfo?.name ||
    vendorInfo?.company_name ||
    vendorInfo?.display_name ||
    invoice.invoice_addressed_to ||
    po?.vendor_contact_name ||
    (invoice.vendor_id != null ? `Vendor #${invoice.vendor_id}` : 'General Vendor')

  const vesselLabel =
    invoice.vessel_name?.trim() ||
    nestedVessel?.fleet_name?.trim() ||
    (invoice.vessel_id != null ? `Vessel #${invoice.vessel_id}` : 'Vessel Expense')

  const vesselImo = invoice.vessel_imo_number || nestedVessel?.imo_number || undefined
  const vesselType = invoice.vessel_type || nestedVessel?.vessel_type || undefined
  const vesselFlag = invoice.vessel_flag || nestedVessel?.flag || undefined

  const curr = (invoice.currency || po?.currency || quotation?.currency || FALLBACK_CURRENCY).toUpperCase()
  const rawAmt = invoice.amount != null ? Number(invoice.amount) : Number(po?.total_amount || 0)
  const poTotalAmount = po?.total_amount != null ? Number(po.total_amount) : 0

  const poFreight = po?.freight_cost != null && Number(po.freight_cost) > 0 ? Number(po.freight_cost) : 0
  const poBarge = po?.barge_cost != null && Number(po.barge_cost) > 0 ? Number(po.barge_cost) : 0
  const poTruck = po?.truck_cost != null && Number(po.truck_cost) > 0 ? Number(po.truck_cost) : 0
  const poPackaging = po?.packaging_cost != null && Number(po.packaging_cost) > 0 ? Number(po.packaging_cost) : 0
  const poOther = po?.other_cost != null && Number(po.other_cost) > 0 ? Number(po.other_cost) : 0
  const poDiscount = po?.total_discount != null && Number(po.total_discount) > 0 ? Number(po.total_discount) : 0
  const poLogisticsTax = po?.vat_on_logistics != null && Number(po.vat_on_logistics) > 0 ? Number(po.vat_on_logistics) : 0
  const poLogisticsTotal = poFreight + poBarge + poTruck + poPackaging + poOther

  const rawLines = quotation?.lines || (invoice as any)?.purchaseOrder?.quotation?.lines || []

  let sumLineSubtotal = 0
  let sumLineTax = 0

  const parsedLineItems: ParsedLineItem[] =
    rawLines.length > 0
      ? rawLines.map((line: any, idx: number) => {
          const effectiveQty =
            line.assigned_quantity != null && Number(line.assigned_quantity) > 0
              ? Number(line.assigned_quantity)
              : rawLines.length === 1 && po?.required_quantity != null && Number(po.required_quantity) > 0
              ? Number(po.required_quantity)
              : Number(line.quantity) || 1

          const unitPriceNum = Number(line.unit_price != null ? line.unit_price : (po?.unit_price ?? 0))
          const discountPct = Number(line.discount_percent ?? line.discount ?? 0)
          const taxPct = Number(line.gst_percent ?? line.tax_percent ?? 0)

          const baseAmt = effectiveQty * unitPriceNum
          const discAmt = (baseAmt * discountPct) / 100
          const netSub = baseAmt - discAmt
          const lineTax = (netSub * taxPct) / 100
          const calculatedTotal = netSub + lineTax

          sumLineSubtotal += netSub
          sumLineTax += lineTax

          return {
            name:
              line.line_item_name ||
              line.manual_item_name ||
              line.item_name ||
              line.part_name ||
              line.part?.name ||
              line.description ||
              po?.description ||
              requisition?.title ||
              requisition?.description ||
              `Item #${idx + 1}`,
            quantity: effectiveQty,
            uom: line.unit_of_measurement || 'PCS',
            unitPrice: formatMoney(unitPriceNum, curr),
            discountPercent: discountPct,
            taxPercent: taxPct,
            total: formatMoney(
              line.company_line_total != null ? Number(line.company_line_total) : calculatedTotal,
              curr
            ),
            accountCode: line.account_code || po?.logistic_account_code || po?.budget_code || undefined,
          }
        })
      : [
          {
            name:
              po?.description ||
              requisition?.title ||
              requisition?.description ||
              invoice.three_way_match_details ||
              'Procurement of marine vessel items and services',
            quantity: po?.required_quantity || po?.quantity || 1,
            uom: 'LOT',
            unitPrice: formatMoney(po?.unit_price ?? rawAmt, curr),
            discountPercent: 0,
            taxPercent: po?.company_tax ? Math.round((po.company_tax / (rawAmt || 1)) * 100) : 0,
            total: formatMoney(rawAmt, curr),
            accountCode: po?.logistic_account_code || requisition?.accounting_code || 'GL-6100',
          },
        ]

  const poLinesSubtotal = sumLineSubtotal
  const poTotalTax = sumLineTax + poLogisticsTax
  const poComputedTotal = poLinesSubtotal + poLogisticsTotal - poDiscount + poTotalTax

  const isPartialBilling =
    poTotalAmount > 0 &&
    rawAmt > 0 &&
    Math.abs(rawAmt - (poTotalAmount || poComputedTotal)) > 0.01 &&
    rawAmt < (poTotalAmount || poComputedTotal)

  let invoiceSubtotal = 0
  let invoiceTax = 0
  let invoiceFreight = 0
  let invoiceBarge = 0
  let invoiceTruck = 0
  let invoicePackaging = 0
  let invoiceOther = 0
  let invoiceDiscount = 0

  if (isPartialBilling) {
    const effectiveTaxRate =
      poComputedTotal > 0 && (poLinesSubtotal + poLogisticsTotal - poDiscount) > 0
        ? poTotalTax / (poLinesSubtotal + poLogisticsTotal - poDiscount)
        : 0.1
    invoiceSubtotal = rawAmt / (1 + effectiveTaxRate)
    invoiceTax = rawAmt - invoiceSubtotal
  } else {
    invoiceSubtotal =
      sumLineSubtotal > 0
        ? sumLineSubtotal
        : po?.company_subtotal != null
        ? Number(po.company_subtotal)
        : rawAmt - (poLogisticsTotal - poDiscount + poTotalTax)
    invoiceFreight = poFreight
    invoiceBarge = poBarge
    invoiceTruck = poTruck
    invoicePackaging = poPackaging
    invoiceOther = poOther
    invoiceDiscount = poDiscount
    invoiceTax =
      poTotalTax > 0
        ? poTotalTax
        : po?.company_tax != null
        ? Number(po.company_tax)
        : 0

    const checkSum =
      invoiceSubtotal +
      invoiceFreight +
      invoiceBarge +
      invoiceTruck +
      invoicePackaging +
      invoiceOther -
      invoiceDiscount +
      invoiceTax
    if (Math.abs(checkSum - rawAmt) > 0.01 && invoiceSubtotal <= 0) {
      invoiceSubtotal = rawAmt - invoiceTax
    }
  }

  const detailsDescription =
    po?.description ||
    requisition?.description ||
    requisition?.title ||
    invoice.three_way_match_details ||
    invoice.approval_remarks ||
    'Marine vessel procurement expense invoice'

  const expenseType = invoice.purchase_order_id != null ? 'Purchase Order' : 'General Expense'
  const is3Way = invoice.three_way_matched === true || Boolean(invoice.goods_receipt_id && invoice.purchase_order_id)

  const paymentTermsResolved =
    invoice.payment_terms ||
    po?.payment_terms ||
    vendorInfo?.payment_terms ||
    'Net 30 Days'

  return {
    id: invoice.id,
    invoiceNumber: invoice.invoice_number?.trim() || `INV-${invoice.id}`,
    vesselName: vesselLabel,
    vesselImo,
    vesselType,
    vesselFlag,
    expenseType,
    date: formatDate(invoice.invoice_date),
    amount: formatMoney(rawAmt, curr),
    rawAmount: rawAmt,
    paidAmount: invoice.paid_amount != null ? formatMoney(invoice.paid_amount, curr) : undefined,
    status: toUiStatus(invoice),
    threeWayMatched: is3Way,
    threeWayMatchDetails: invoice.three_way_match_details || undefined,
    approvalRemarks: invoice.approval_remarks || undefined,
    details: {
      vendor: vendorLabel,
      vendorCompanyName: vendorInfo?.company_name || vendorLabel,
      vendorContact: vendorInfo?.contact_person || po?.vendor_contact_name || undefined,
      vendorEmail: vendorInfo?.email || po?.vendor_contact_email || undefined,
      vendorPhone: vendorInfo?.phone || po?.vendor_contact_phone || undefined,
      vendorPan: vendorInfo?.pan || undefined,
      vendorGst: vendorInfo?.gst_number || undefined,
      vendorAddress: vendorInfo?.address || po?.vendor_address || undefined,
      description: detailsDescription,
      currency: curr,
      port: po?.delivery_terms || po?.location_of_delivery || 'Port of Operation',
      paymentTerms: paymentTermsResolved,
    },
    poDetails: {
      poNumber: po?.po_number || (invoice.purchase_order_id ? `PO-${invoice.purchase_order_id}` : undefined),
      poDate: po?.po_date ? formatDate(po.po_date) : undefined,
      poStatus: po?.status || undefined,
      poAmount: poTotalAmount > 0 ? formatMoney(poTotalAmount, curr) : undefined,
      isPartialBilling,
      rawPoAmount: poTotalAmount,
      bargeCost: invoiceBarge > 0 ? formatMoney(invoiceBarge, curr) : undefined,
      freightCost: invoiceFreight > 0 ? formatMoney(invoiceFreight, curr) : undefined,
      truckCost: invoiceTruck > 0 ? formatMoney(invoiceTruck, curr) : undefined,
      packagingCost: invoicePackaging > 0 ? formatMoney(invoicePackaging, curr) : undefined,
      otherCost: invoiceOther > 0 ? formatMoney(invoiceOther, curr) : undefined,
      totalDiscount: invoiceDiscount > 0 ? formatMoney(invoiceDiscount, curr) : undefined,
      budgetCode: po?.budget_code || 'OPEX-VESSEL-2026',
      glAccountCode: po?.logistic_account_code || requisition?.accounting_code || 'GL-6100 (Marine Procurement)',
      glSubAccountCode: po?.logistic_subaccount_code || requisition?.sub_accounting_code || 'SUB-104 (Spares & Consumables)',
      accountCategory: po?.account_category || requisition?.inventory_item_head || 'Vessel Operational Expense',
      subtotal: formatMoney(invoiceSubtotal, curr),
      taxAmount: formatMoney(invoiceTax, curr),
      grandTotal: formatMoney(rawAmt, curr),
    },
    reqDetails: requisition
      ? {
          reqCode: requisition.requisition_code || `REQ-${requisition.id}`,
          reqTitle: requisition.title || undefined,
          urgency: requisition.urgency || undefined,
          justification: requisition.justification || undefined,
        }
      : undefined,
    rfqCode: rfq?.rfq_code || undefined,
    grnDetails: invoice.goods_receipt
      ? {
          grnId: invoice.goods_receipt.id,
          receivedDate: invoice.goods_receipt.received_date ? formatDate(invoice.goods_receipt.received_date) : undefined,
          status: invoice.goods_receipt.status || 'Verified on board',
          discrepancy: invoice.goods_receipt.discrepancy || 'None (Full receipt confirmed)',
          receivedQty: invoice.goods_receipt.received_qty,
          generalNotes: invoice.goods_receipt.general_notes || undefined,
        }
      : undefined,
    lineItems: parsedLineItems,
    approvedBy:
      invoice.approved_by_user?.username ||
      (invoice.approved_by != null ? `User #${invoice.approved_by}` : undefined),
    approvedAt: invoice.approved_at ? formatDate(invoice.approved_at) : undefined,
    rejectedBy:
      invoice.rejected_by_user?.username ||
      (invoice.rejected_by != null ? `User #${invoice.rejected_by}` : undefined),
    rejectedAt: invoice.rejected_at ? formatDate(invoice.rejected_at) : undefined,
    transactionId: invoice.transaction_id ?? undefined,
    attachmentUrl: resolveAttachmentUrl(invoice.attachment_url || invoice.preview_url),
    paymentTerms: paymentTermsResolved,
    purchaseOrderId: invoice.purchase_order_id ?? null,
    goodsReceiptId: invoice.goods_receipt_id ?? null,
    vendorId: invoice.vendor_id ?? null,
    submittedAt: invoice.submitted_at ?? null,
    rawInvoice: invoice,
  }
}

const extractErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as {response?: {data?: {message?: string}}}).response
    if (response?.data?.message) return response.data.message
  }
  if (error instanceof Error && error.message) return error.message
  return fallback
}

export default function AccountsVesselExpenseInvoice() {
  const {hasPermission} = useAuth()
  const [invoices, setInvoices] = useState<VesselExpenseInvoice[]>([])
  const [selectedInvoice, setSelectedInvoice] = useState<VesselExpenseInvoice | null>(null)
  const [activeModalTab, setActiveModalTab] = useState<'details' | 'matching' | 'journey'>('details')
  const [copiedTxn, setCopiedTxn] = useState(false)
  const [approvalModalData, setApprovalModalData] = useState<ApprovalModalData | null>(null)
  const [rejectModalData, setRejectModalData] = useState<RejectModalData | null>(null)
  const [journeyInvoice, setJourneyInvoice] = useState<VesselExpenseInvoice | null>(null)
  const [journeyInfo, setJourneyInfo] = useState<InvoiceJourneyData | null>(null)
  const [txnInput, setTxnInput] = useState('')
  const [approvalFile, setApprovalFile] = useState<File | null>(null)
  const [rejectRemarks, setRejectRemarks] = useState('')
  const [rejectFile, setRejectFile] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [journeyLoading, setJourneyLoading] = useState(false)
  const [journeyError, setJourneyError] = useState<string | null>(null)
  const [viewMoreStep, setViewMoreStep] = useState<string | null>(null)
  const [viewMoreData, setViewMoreData] = useState<any>(null)
  const [viewMoreLoading, setViewMoreLoading] = useState(false)
  const [procurementAttachments, setProcurementAttachments] = useState<any[]>([])
  const [attachmentsLoading, setAttachmentsLoading] = useState(false)
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<number | string | null>(null)
  const {state: globalState, vessels: globalVessels} = useGlobalFilter()

  const globalVesselIds = useMemo(
    () =>
      globalVessels
        .map((v: any) => v.id)
        .sort()
        .join(','),
    [globalVessels]
  )

  const [statusFilter, setStatusFilter] = useState<'ALL' | 'Approved' | 'Paid' | 'Rejected'>('ALL')
  const [searchQuery, setSearchQuery] = useState('')

  const filterCounts = useMemo(() => {
    return {
      ALL: invoices.length,
      Approved: invoices.filter((i) => i.status === 'Approved').length,
      Paid: invoices.filter((i) => i.status === 'Paid').length,
      Rejected: invoices.filter((i) => i.status === 'Rejected').length,
    }
  }, [invoices])

  const filteredInvoices = useMemo(() => {
    let result = invoices
    if (statusFilter !== 'ALL') {
      result = result.filter((inv) => inv.status === statusFilter)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      result = result.filter((inv) =>
        inv.invoiceNumber.toLowerCase().includes(q) ||
        inv.vesselName.toLowerCase().includes(q) ||
        inv.details.vendor.toLowerCase().includes(q) ||
        inv.amount.toLowerCase().includes(q) ||
        inv.date.toLowerCase().includes(q) ||
        (inv.poDetails?.poNumber || '').toLowerCase().includes(q) ||
        (inv.reqDetails?.reqCode || '').toLowerCase().includes(q) ||
        (inv.details.description || '').toLowerCase().includes(q)
      )
    }
    return result
  }, [invoices, statusFilter, searchQuery])

  const hasActionableInvoices = useMemo(
    () => filteredInvoices.some((inv) => inv.status === 'Approved'),
    [filteredInvoices]
  )

  const handleCopyTxn = (txn?: string) => {
    if (!txn) return
    navigator.clipboard.writeText(txn)
    setCopiedTxn(true)
    setTimeout(() => setCopiedTxn(false), 2000)
  }

  const handleViewMore = async (phase: string) => {
    setViewMoreStep(phase)
    setViewMoreData(null)
    setViewMoreLoading(true)
    try {
      if (phase === 'Material Requisition' && journeyInfo?.requisition?.id) {
        const data = await getRequisitionById(journeyInfo.requisition.id)
        setViewMoreData(data)
      } else if (phase === 'RFQ Broadcast' && journeyInfo?.rfq?.id) {
        const data = await getRfqById(journeyInfo.rfq.id)
        setViewMoreData(data)
      } else if (phase === 'Quotation Evaluation' && journeyInfo?.quotation?.id) {
        const data = await getQuotationById(journeyInfo.quotation.id)
        setViewMoreData(data)
      } else if (phase === 'Purchase Order' && journeyInfo?.po?.id) {
        const data = await getPurchaseOrderById(journeyInfo.po.id)
        setViewMoreData(data)
      } else if (phase === 'Goods Receipt (GRN)' && journeyInfo?.goodReceipt?.id) {
        const data = await getGoodsReceiptById(journeyInfo.goodReceipt.id)
        setViewMoreData(data)
      } else if (phase === 'Invoice & Payment') {
        setViewMoreData(journeyInfo?.invoice)
      }
    } catch (error) {
      console.error('Failed to load details', error)
    } finally {
      setViewMoreLoading(false)
    }
  }

  useEffect(() => {
    const loadInvoices = async () => {
      setIsLoading(true)
      setLoadError(null)
      try {
        const params = new URLSearchParams()
        if (globalState.company) {
          params.append('companyId', globalState.company.id.toString())
        }
        if (globalState.vessel) {
          params.append('vesselId', globalState.vessel.id.toString())
        } else if (globalState.company && globalVessels.length > 0) {
          globalVessels.forEach((v: any) => params.append('vesselId', v.id.toString()))
        }

        let data = await getVesselInvoices(params)
        if (!Array.isArray(data)) data = []

        // In the invoices, only keep those which are approved, rejected, and paid
        data = data.filter((i: any) => {
          const st = toUiStatus(i)
          return st === 'Approved' || st === 'Paid' || st === 'Rejected'
        })

        if (globalState.vessel) {
          data = data.filter((i: any) => {
            const vId =
              i.vessel_id ??
              i.purchaseOrder?.quotation?.rfq?.requisition?.vessel?.id ??
              i.purchaseOrder?.quotation?.rfq?.requisition?.vessel_id
            return !vId || String(vId) === String(globalState.vessel!.id)
          })
        } else if (globalState.company && globalVessels.length > 0) {
          const vIds = new Set(globalVessels.map((v: any) => String(v.id)))
          data = data.filter((i: any) => {
            const vId =
              i.vessel_id ??
              i.purchaseOrder?.quotation?.rfq?.requisition?.vessel?.id ??
              i.purchaseOrder?.quotation?.rfq?.requisition?.vessel_id
            return !vId || vIds.has(String(vId))
          })
        }

        setInvoices(data.map(mapInvoiceToUi))
      } catch (error) {
        setLoadError(extractErrorMessage(error, 'Unable to load invoices.'))
      } finally {
        setIsLoading(false)
      }
    }
    void loadInvoices()
  }, [globalState.company?.id, globalState.vessel?.id, globalVesselIds])

  // Fetch procurement attachments when an invoice is selected for detail view
  useEffect(() => {
    if (!selectedInvoice) {
      setProcurementAttachments([])
      return
    }
    let cancelled = false
    const fetchAttachments = async () => {
      setAttachmentsLoading(true)
      try {
        const resp = await procurementApi.get(`/invoices/${selectedInvoice.id}`)
        if (!cancelled) {
          setProcurementAttachments(resp.data?.attachments || [])
        }
      } catch (err) {
        console.error('Failed to fetch procurement attachments', err)
        if (!cancelled) {
          setProcurementAttachments([])
        }
      } finally {
        if (!cancelled) {
          setAttachmentsLoading(false)
        }
      }
    }
    void fetchAttachments()
    return () => {
      cancelled = true
    }
  }, [selectedInvoice?.id])

  const applyInvoiceUpdate = (rawInvoice: VesselInvoiceApi | null) => {
    if (!rawInvoice) return
    const mapped = mapInvoiceToUi(rawInvoice)
    setInvoices((prev) => prev.map((inv) => (inv.id === mapped.id ? {...inv, ...mapped} : inv)))
    setSelectedInvoice((prev) => (prev && prev.id === mapped.id ? {...prev, ...mapped} : prev))
    setJourneyInvoice((prev) => (prev && prev.id === mapped.id ? {...prev, ...mapped} : prev))
  }

  const openApprovalModal = (id: number) => {
    setApprovalModalData({id})
    setActionError(null)
  }

  const closeApprovalModal = () => {
    setApprovalModalData(null)
    setTxnInput('')
    setApprovalFile(null)
    setActionError(null)
  }

  const openRejectModal = (id: number) => {
    setRejectModalData({id})
    setRejectRemarks('')
    setRejectFile(null)
    setActionError(null)
  }

  const closeRejectModal = () => {
    setRejectModalData(null)
    setRejectRemarks('')
    setRejectFile(null)
    setActionError(null)
  }

  const submitApproval = async () => {
    if (!approvalModalData || !txnInput.trim()) return
    setIsSubmitting(true)
    setActionError(null)
    try {
      const updated = await updateVesselInvoiceStatus(approvalModalData.id, {
        status: 'APPROVED',
        transactionId: txnInput.trim(),
        attachment: approvalFile,
      })
      applyInvoiceUpdate(updated)
      closeApprovalModal()
    } catch (error) {
      setActionError(extractErrorMessage(error, 'Unable to approve invoice.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const submitReject = async () => {
    if (!rejectModalData || !rejectRemarks.trim()) return
    setIsSubmitting(true)
    setActionError(null)
    try {
      const updated = await updateVesselInvoiceStatus(rejectModalData.id, {
        status: 'REJECTED',
        remarks: rejectRemarks.trim(),
        attachment: rejectFile,
      })
      applyInvoiceUpdate(updated)
      closeRejectModal()
    } catch (error) {
      setActionError(extractErrorMessage(error, 'Unable to reject invoice.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const closeModal = () => {
    setSelectedInvoice(null)
    setActiveModalTab('details')
    setJourneyInvoice(null)
    setJourneyInfo(null)
    setJourneyError(null)
    setJourneyLoading(false)
  }

  const closeJourney = () => {
    setJourneyInvoice(null)
    setJourneyInfo(null)
    setJourneyError(null)
    setJourneyLoading(false)
  }

  const openJourney = async (invoice: VesselExpenseInvoice) => {
    setJourneyInvoice(invoice)
    setJourneyInfo(null)
    setJourneyError(null)
    setJourneyLoading(true)
    try {
      const data = await getVesselInvoiceJourney(invoice.id)
      setJourneyInfo(data)
      if (data?.invoice) {
        const mapped = mapInvoiceToUi(data.invoice)
        const approvedBy = data.invoice.approved_by_user?.username
        const rejectedBy = data.invoice.rejected_by_user?.username
        setJourneyInvoice((prev) =>
          prev && prev.id === mapped.id
            ? {
                ...prev,
                ...mapped,
                approvedBy: approvedBy ?? prev.approvedBy,
                rejectedBy: rejectedBy ?? prev.rejectedBy,
              }
            : prev
        )
        setSelectedInvoice((prev) =>
          prev && prev.id === mapped.id
            ? {
                ...prev,
                ...mapped,
                approvedBy: approvedBy ?? prev.approvedBy,
                rejectedBy: rejectedBy ?? prev.rejectedBy,
              }
            : prev
        )
      }
    } catch (error) {
      setJourneyError(extractErrorMessage(error, 'Unable to load invoice journey.'))
    } finally {
      setJourneyLoading(false)
    }
  }

  const formatRequisitionValue = (value: unknown): string => {
    if (value == null) return '-'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '-'
    const text = String(value).trim()
    return text.length > 0 ? text : '-'
  }

  // Clean, structured detail renderers with NO terms & conditions text walls
  const renderRequisitionMoreInfo = () => {
    const requisition = viewMoreData ?? null
    if (viewMoreLoading)
      return (
        <div className='p-8 text-center text-sm text-slate-500 flex justify-center items-center gap-2'>
          <Loader className='w-5 h-5 animate-spin text-blue-600' />
          <span>Loading requisition details...</span>
        </div>
      )
    if (!requisition) return null

    return (
      <div className='space-y-4'>
        <div className='bg-blue-50/60 p-4 rounded-xl border border-blue-100 flex items-start gap-3'>
          <ClipboardList className='w-5 h-5 text-blue-600 shrink-0 mt-0.5' />
          <div>
            <div className='font-bold text-slate-900 text-sm'>
              {requisition.requisition_code || `REQ-${requisition.id}`} • {requisition.title || 'Marine Requisition'}
            </div>
            <div className='text-xs text-slate-600 mt-1 leading-relaxed'>
              {requisition.description || requisition.justification || 'Required for vessel operations and maintenance.'}
            </div>
          </div>
        </div>

        <div className='grid grid-cols-2 sm:grid-cols-3 gap-3'>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Urgency</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5'>{formatRequisitionValue(requisition.urgency)}</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Quantity / UOM</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5'>{formatRequisitionValue(requisition.quantity)} {formatRequisitionValue(requisition.uom)}</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Estimated Rate</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5'>
              {requisition.estimated_unit_price != null ? formatMoney(requisition.estimated_unit_price, selectedInvoice?.details.currency) : '-'}
            </div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>GL Account Code</div>
            <div className='font-mono font-bold text-indigo-700 text-xs mt-0.5'>{formatRequisitionValue(requisition.accounting_code || 'GL-6100')}</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Sub Account</div>
            <div className='font-mono font-bold text-slate-700 text-xs mt-0.5'>{formatRequisitionValue(requisition.sub_accounting_code || 'SUB-104')}</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Inventory Head</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5'>{formatRequisitionValue(requisition.inventory_item_head || 'Marine Spares')}</div>
          </div>
        </div>

        <div className='p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between text-xs'>
          <span className='text-slate-500 font-medium'>Vessel / Shore Authorization:</span>
          <span className='font-bold text-emerald-600 flex items-center gap-1'>
            <CheckCircle2 className='w-4 h-4' /> Fully Approved for RFQ
          </span>
        </div>
      </div>
    )
  }

  const renderRfqMoreInfo = () => {
    const rfq = viewMoreData ?? null
    if (viewMoreLoading)
      return (
        <div className='p-8 text-center text-sm text-slate-500 flex justify-center items-center gap-2'>
          <Loader className='w-5 h-5 animate-spin text-blue-600' />
          <span>Loading RFQ details...</span>
        </div>
      )
    if (!rfq) return null

    return (
      <div className='space-y-4'>
        <div className='bg-indigo-50/60 p-4 rounded-xl border border-indigo-100 flex items-start gap-3'>
          <Send className='w-5 h-5 text-indigo-600 shrink-0 mt-0.5' />
          <div>
            <div className='font-bold text-slate-900 text-sm'>
              {rfq.rfq_code || `RFQ-${rfq.id}`} • {rfq.title || 'Vendor RFQ Broadcast'}
            </div>
            <div className='text-xs text-slate-600 mt-1 leading-relaxed'>
              {rfq.item_description || rfq.technical_specifications || 'Vendor Request for Quotation broadcasted across certified suppliers.'}
            </div>
          </div>
        </div>

        <div className='grid grid-cols-2 gap-3'>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Broadcast Status</div>
            <div className='font-bold text-emerald-700 text-xs mt-0.5'>{formatRequisitionValue(rfq.status || 'Completed')}</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Response Due Date</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5'>{formatDate(rfq.response_due_date)}</div>
          </div>
        </div>
      </div>
    )
  }

  const renderQuotationMoreInfo = () => {
    const quotation = viewMoreData ?? null
    if (viewMoreLoading)
      return (
        <div className='p-8 text-center text-sm text-slate-500 flex justify-center items-center gap-2'>
          <Loader className='w-5 h-5 animate-spin text-blue-600' />
          <span>Loading quotation evaluation...</span>
        </div>
      )
    if (!quotation) return null

    return (
      <div className='space-y-4'>
        <div className='bg-emerald-50/60 p-4 rounded-xl border border-emerald-100 flex items-start gap-3'>
          <FileText className='w-5 h-5 text-emerald-600 shrink-0 mt-0.5' />
          <div>
            <div className='font-bold text-slate-900 text-sm'>
              Quotation Evaluation • Status: <span className='text-emerald-700 font-extrabold'>{quotation.status || 'Selected'}</span>
            </div>
            <div className='text-xs text-slate-600 mt-1'>
              Quoted Total: <strong className='font-mono text-slate-900 text-sm'>{formatMoney(quotation.total_amount, quotation.currency)}</strong>
            </div>
          </div>
        </div>

        <div className='grid grid-cols-2 sm:grid-cols-3 gap-3'>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Unit Price</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5'>{formatMoney(quotation.unit_price, quotation.currency)}</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Quoted Quantity</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5'>{formatRequisitionValue(quotation.quantity || quotation.actual_quantity_assigned_to_vendor || 1)}</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Estimated Delivery</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5'>{formatDate(quotation.promised_delivery_date || quotation.estimated_delivery_date)}</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Delivery Location</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5'>{formatRequisitionValue(quotation.location_of_delivery || 'Port of Operation')}</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Payment Terms</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5'>Net 30 Days</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Freight & Logistics</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5'>
              {quotation.freight_cost ? formatMoney(quotation.freight_cost, quotation.currency) : 'Included'}
            </div>
          </div>
        </div>

        {quotation.lines && quotation.lines.length > 0 && (
          <div className='bg-white rounded-xl border border-slate-200 overflow-hidden'>
            <div className='px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-700 flex justify-between items-center'>
              <span>Quoted Line Items</span>
              <span className='text-[11px] font-medium text-slate-500'>{quotation.lines.length} Item(s)</span>
            </div>
            <div className='divide-y divide-slate-100 text-xs max-h-48 overflow-y-auto'>
              {quotation.lines.map((l: any, i: number) => (
                <div key={i} className='px-4 py-2.5 flex items-center justify-between hover:bg-slate-50/60'>
                  <div>
                    <div className='font-bold text-slate-900'>{l.line_item_name || l.item_name || `Item #${i + 1}`}</div>
                    <div className='text-[11px] text-slate-500 font-mono mt-0.5'>
                      {l.quantity} {l.unit_of_measurement || 'PCS'} • Rate: {formatMoney(l.unit_price, quotation.currency)}
                    </div>
                  </div>
                  <div className='font-extrabold text-slate-900 font-mono'>
                    {formatMoney(l.company_line_total != null ? l.company_line_total : (l.quantity || 1) * (l.unit_price || 0), quotation.currency)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderPoMoreInfo = () => {
    const po = viewMoreData ?? null
    if (viewMoreLoading)
      return (
        <div className='p-8 text-center text-sm text-slate-500 flex justify-center items-center gap-2'>
          <Loader className='w-5 h-5 animate-spin text-blue-600' />
          <span>Loading purchase order details...</span>
        </div>
      )
    if (!po) return null

    return (
      <div className='space-y-4'>
        <div className='bg-blue-50/60 p-4 rounded-xl border border-blue-100 flex items-start gap-3'>
          <ShoppingCart className='w-5 h-5 text-blue-600 shrink-0 mt-0.5' />
          <div>
            <div className='font-bold text-slate-900 text-sm'>
              Purchase Order: <span className='font-mono'>{po.po_number || `PO-${po.id}`}</span>
            </div>
            <div className='text-xs text-slate-600 mt-1'>
              PO Total: <strong className='font-mono text-slate-900 text-sm'>{formatMoney(po.total_amount, po.currency)}</strong> • Date: {formatDate(po.po_date)}
            </div>
          </div>
        </div>

        <div className='grid grid-cols-2 sm:grid-cols-3 gap-3'>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>PO Status</div>
            <div className='font-bold text-emerald-600 text-xs mt-0.5'>{formatRequisitionValue(po.status || 'Confirmed')}</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Vendor Contact</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5 truncate'>{formatRequisitionValue(po.vendor_contact_name || 'Vendor Rep')}</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Delivery Date</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5'>{formatDate(po.delivery_date || po.estimated_arrival_date)}</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Budget Code</div>
            <div className='font-mono font-bold text-slate-800 text-xs mt-0.5'>{formatRequisitionValue(po.budget_code || 'OPEX-VESSEL-2026')}</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Payment Terms</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5'>Net 30 Days</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Delivery Terms</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5'>{formatRequisitionValue(po.delivery_terms || 'CIF Port')}</div>
          </div>
        </div>
      </div>
    )
  }

  const renderGoodsReceiptMoreInfo = () => {
    const grn = viewMoreData ?? null
    if (viewMoreLoading)
      return (
        <div className='p-8 text-center text-sm text-slate-500 flex justify-center items-center gap-2'>
          <Loader className='w-5 h-5 animate-spin text-blue-600' />
          <span>Loading goods receipt details...</span>
        </div>
      )
    if (!grn) return null

    return (
      <div className='space-y-4'>
        <div className='bg-emerald-50/60 p-4 rounded-xl border border-emerald-100 flex items-start gap-3'>
          <PackageCheck className='w-5 h-5 text-emerald-600 shrink-0 mt-0.5' />
          <div>
            <div className='font-bold text-slate-900 text-sm'>
              Goods Receipt (GRN) • Status: <span className='text-emerald-700 font-extrabold'>{grn.status || 'Verified on board'}</span>
            </div>
            <div className='text-xs text-slate-600 mt-1'>
              Received Date: {formatDate(grn.received_date)} • Discrepancy: <strong className='text-emerald-700'>{grn.discrepancy || '0 Variance Reported'}</strong>
            </div>
          </div>
        </div>

        <div className='grid grid-cols-2 gap-3'>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Receipt Condition</div>
            <div className='font-bold text-emerald-600 text-xs mt-0.5'>100% Intact & Accepted</div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Inspecting Officer</div>
            <div className='font-bold text-slate-900 text-xs mt-0.5'>Chief Engineer / Vessel Master</div>
          </div>
        </div>
      </div>
    )
  }

  const renderInvoiceMoreInfo = () => {
    const inv = viewMoreData ?? selectedInvoice?.rawInvoice ?? null
    if (viewMoreLoading)
      return (
        <div className='p-8 text-center text-sm text-slate-500 flex justify-center items-center gap-2'>
          <Loader className='w-5 h-5 animate-spin text-blue-600' />
          <span>Loading invoice settlement details...</span>
        </div>
      )
    if (!inv) return null

    return (
      <div className='space-y-4'>
        <div className='bg-indigo-50/60 p-4 rounded-xl border border-indigo-100 flex items-start gap-3'>
          <Receipt className='w-5 h-5 text-indigo-600 shrink-0 mt-0.5' />
          <div>
            <div className='font-bold text-slate-900 text-sm'>
              Invoice & Settlement • <span className='font-mono'>{inv.invoice_number || `INV-${inv.id}`}</span>
            </div>
            <div className='text-xs text-slate-600 mt-1'>
              Total Amount: <strong className='font-mono text-slate-900 text-sm'>{formatMoney(inv.amount, inv.currency)}</strong>
            </div>
          </div>
        </div>

        <div className='grid grid-cols-2 gap-3'>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>3-Way Match Verification</div>
            <div className='font-bold text-emerald-600 text-xs mt-0.5 flex items-center gap-1'>
              <CheckCircle2 className='w-3.5 h-3.5' /> Clean Verification Passed
            </div>
          </div>
          <div className='p-3 bg-slate-50 rounded-xl border border-slate-200'>
            <div className='text-[10px] uppercase font-bold text-slate-400'>Transaction Ref / UTR</div>
            <div className='font-mono font-bold text-slate-800 text-xs mt-0.5'>{formatRequisitionValue(inv.transaction_id || selectedInvoice?.transactionId)}</div>
          </div>
        </div>
      </div>
    )
  }

  const getJourneySteps = (
    invoice: VesselExpenseInvoice | null,
    journey: InvoiceJourneyData | null
  ) => {
    const requisition = journey?.requisition ?? null
    const rfq = journey?.rfq ?? null
    const quotation = journey?.quotation ?? null
    const po = journey?.po ?? null
    const goodReceipt = journey?.goodReceipt ?? null
    const journeyInvoice = journey?.invoice ?? null

    const invoiceActor =
      journeyInvoice?.approved_by_user?.username ||
      journeyInvoice?.rejected_by_user?.username ||
      invoice?.approvedBy ||
      invoice?.rejectedBy ||
      'Accounts'

    const baseSteps: Array<{
      phase: string
      date: string | null
      actor: string
      icon: React.ComponentType<any>
      refCode?: string
      amount?: string
      status?: string
      badge?: string
      meta?: string
    }> = [
      {
        phase: 'Material Requisition',
        date: requisition?.created_at || null,
        actor: requisition?.created_by != null ? `User #${requisition.created_by}` : 'Vessel Officer',
        icon: ClipboardList,
        refCode: requisition?.requisition_code || (requisition?.id ? `REQ-${requisition.id}` : undefined),
        status: requisition?.status || 'Approved',
        badge: requisition?.urgency || 'Normal',
        meta: requisition?.title || requisition?.description || undefined,
      },
      {
        phase: 'RFQ Broadcast',
        date: rfq?.response_due_date || rfq?.created_at || null,
        actor: 'Procurement / Purchasing',
        icon: Send,
        refCode: rfq?.rfq_code || (rfq?.id ? `RFQ-${rfq.id}` : undefined),
        status: rfq?.status || 'Sent',
        meta: rfq?.title || rfq?.item_description || undefined,
      },
      {
        phase: 'Quotation Evaluation',
        date: quotation?.promised_delivery_date || quotation?.created_at || null,
        actor: 'Vendor / Supplier',
        icon: FileText,
        refCode: quotation?.id ? `QT-${quotation.id}` : undefined,
        amount: quotation?.total_amount != null ? formatMoney(quotation.total_amount, quotation.currency) : undefined,
        status: quotation?.status || 'Selected',
        meta: quotation?.location_of_delivery || undefined,
      },
      {
        phase: 'Purchase Order',
        date: po?.po_date || null,
        actor: po?.po_number ? `PO ${po.po_number}` : 'Purchasing Dept',
        icon: ShoppingCart,
        refCode: po?.po_number || (po?.id ? `PO-${po.id}` : undefined),
        amount: po?.total_amount != null ? formatMoney(po.total_amount, po.currency) : undefined,
        status: po?.status || 'Confirmed',
        meta: po?.delivery_terms || undefined,
      },
      {
        phase: 'Goods Receipt (GRN)',
        date: goodReceipt?.received_date || null,
        actor: goodReceipt?.received_by != null ? `Officer #${goodReceipt.received_by}` : 'Chief Engineer',
        icon: PackageCheck,
        refCode: goodReceipt?.id ? `GRN-${goodReceipt.id}` : undefined,
        status: goodReceipt?.status || (goodReceipt ? 'Received' : 'Pending'),
        badge: goodReceipt?.discrepancy || 'No Variance',
        meta: goodReceipt?.general_notes || undefined,
      },
      {
        phase: 'Invoice & Payment',
        date:
          journeyInvoice?.approved_at ||
          journeyInvoice?.rejected_at ||
          journeyInvoice?.invoice_date ||
          invoice?.rawInvoice?.invoice_date ||
          null,
        actor: invoiceActor,
        icon: Receipt,
        refCode: invoice?.invoiceNumber,
        amount: invoice?.amount,
        status: invoice?.status || 'Pending',
        meta: invoice?.transactionId ? `TXN: ${invoice.transactionId}` : undefined,
      },
    ]

    const fallbackIndex = Math.min(4, baseSteps.length - 1)
    const currentIndex =
      invoice?.status === 'Approved'
        ? baseSteps.length - 1
        : invoice?.status === 'Rejected'
        ? baseSteps.length - 1
        : fallbackIndex

    return baseSteps.map((step, idx) => ({
      ...step,
      state: idx < currentIndex ? 'completed' : idx === currentIndex ? 'current' : 'upcoming',
    }))
  }

  const parseJourneyDate = (value?: string | null) => {
    if (!value) return null
    const normalized = value.includes('T') ? value : `${value.replace(' ', 'T')}:00`
    const parsed = new Date(normalized)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  const journeyBusy = journeyLoading && journeyInvoice?.id === selectedInvoice?.id

  return (
    <>
      <div className='flex h-screen w-full overflow-hidden bg-slate-50'>
        <div id='sidebar-overlay' className='fixed inset-0 bg-black/50 z-40 hidden md:hidden'></div>

        <main className='flex-1 flex flex-col h-full overflow-hidden relative'>
          <div
            className='flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 pb-24 md:pb-28'
            id='main-scroll'
          >
            <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6'>
              <div>
                <div className='text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1'>
                  Accounts / Vessel Expense
                </div>
                <h1 className='text-2xl font-bold text-slate-900 leading-tight'>
                  Vessel Expense Invoices
                </h1>
                <p className='text-sm text-slate-500'>
                  Review, audit and verify vessel procurement & service invoices
                </p>
              </div>
            </div>

            {loadError && (
              <div className='mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center gap-2'>
                <AlertTriangle className='w-4 h-4 shrink-0' />
                {loadError}
              </div>
            )}

            {/* Status Filter Tabs */}
            <div className='flex items-center gap-2.5 mb-5 overflow-x-auto pb-1'>
              {[
                {
                  key: 'ALL',
                  label: 'All Invoices',
                  countColor: 'bg-slate-100 text-slate-700',
                  activeClass: 'bg-slate-900 text-white border-slate-900 shadow-sm',
                  inactiveClass: 'bg-white text-slate-600 hover:text-slate-900 hover:bg-slate-50 border-slate-200',
                },
                {
                  key: 'Approved',
                  label: 'Approved',
                  countColor: 'bg-emerald-100 text-emerald-900 font-black border border-emerald-300',
                  activeClass: 'bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-600/30 ring-2 ring-emerald-400/30',
                  inactiveClass: 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 font-bold',
                },
                {
                  key: 'Paid',
                  label: 'Paid',
                  countColor: 'bg-teal-100 text-teal-900 font-black border border-teal-300',
                  activeClass: 'bg-teal-700 text-white border-teal-700 shadow-md shadow-teal-700/30 ring-2 ring-teal-400/30',
                  inactiveClass: 'border-teal-300 bg-teal-50 text-teal-800 hover:bg-teal-100 font-bold',
                },
                {
                  key: 'Rejected',
                  label: 'Rejected',
                  countColor: 'bg-red-100 text-red-700 font-bold border border-red-300',
                  activeClass: 'bg-red-600 text-white border-red-600 shadow-md shadow-red-600/30 ring-2 ring-red-400/30',
                  inactiveClass: 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100 font-bold',
                },
              ].map((tab) => {
                const isActive = statusFilter === tab.key
                const count = filterCounts[tab.key as keyof typeof filterCounts] || 0
                return (
                  <button
                    key={tab.key}
                    onClick={() => setStatusFilter(tab.key as any)}
                    className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 border ${
                      isActive ? tab.activeClass : tab.inactiveClass
                    }`}
                  >
                    <span>{tab.label}</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] font-extrabold ${
                        isActive ? 'bg-white/25 text-white' : tab.countColor
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                )
              })}
            </div>

            {/* Search Bar */}
            <div className='mb-5'>
              <div className='relative'>
                <Search className='absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
                <input
                  type='text'
                  placeholder='Search by invoice #, vessel, vendor, PO, requisition, amount…'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='w-full pl-10 pr-10 py-2.5 text-sm rounded-xl border border-slate-200 bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 transition-all'
                />
                {searchQuery && (
                  <button
                    type='button'
                    onClick={() => setSearchQuery('')}
                    className='absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors'
                  >
                    <X className='w-4 h-4' />
                  </button>
                )}
              </div>
            </div>

            {/* Invoices Table */}
            <div className='bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden'>
              <div className='overflow-x-auto'>
                <table className='w-full text-left min-w-225'>
                  <thead>
                    <tr className='bg-slate-50/80 border-b border-slate-200 text-[10px] uppercase font-bold text-slate-400 tracking-wider'>
                      <th className='px-5 py-3.5'>Invoice #</th>
                      <th className='px-5 py-3.5'>Vessel</th>
                      <th className='px-5 py-3.5'>Vendor / Payee</th>
                      <th className='px-5 py-3.5'>Date</th>
                      <th className='px-5 py-3.5 text-right'>Amount</th>
                      <th className='px-5 py-3.5 text-center'>Invoice Status</th>
                      <th className='px-5 py-3.5 text-center'>Actions / Settlement</th>
                    </tr>
                  </thead>
                  <tbody className='text-sm divide-y divide-slate-100'>
                    {isLoading ? (
                      <tr>
                        <td colSpan={7} className='px-5 py-12 text-center text-sm text-slate-500'>
                          <div className='flex items-center justify-center gap-2'>
                            <Loader className='w-5 h-5 animate-spin text-blue-600' />
                            <span>Loading invoices...</span>
                          </div>
                        </td>
                      </tr>
                    ) : filteredInvoices.length === 0 ? (
                      <tr>
                        <td colSpan={7} className='px-5 py-12 text-center text-sm text-slate-500'>
                          {searchQuery.trim()
                            ? `No invoices matching "${searchQuery.trim()}"${statusFilter !== 'ALL' ? ` in ${statusFilter.toLowerCase()}` : ''}.`
                            : `No ${statusFilter !== 'ALL' ? statusFilter.toLowerCase() : ''} invoices found for this criteria.`}
                        </td>
                      </tr>
                    ) : (
                      filteredInvoices.map((inv) => {
                        return (
                          <tr
                            key={inv.id}
                            className='hover:bg-slate-50/80 transition-colors cursor-pointer group'
                            onClick={() => {
                              setSelectedInvoice(inv)
                              setActiveModalTab('details')
                            }}
                          >
                            <td className='px-5 py-4'>
                              <span className='font-mono font-bold text-blue-600 group-hover:underline flex items-center gap-1.5'>
                                <FileText className='w-4 h-4 text-slate-400 group-hover:text-blue-500' />
                                {inv.invoiceNumber}
                              </span>
                            </td>
                            <td className='px-5 py-4'>
                              <div className='font-bold text-slate-800 flex items-center gap-1.5'>
                                <Ship className='w-3.5 h-3.5 text-slate-400' />
                                {inv.vesselName}
                              </div>
                              {inv.vesselImo && (
                                <span className='text-[11px] text-slate-400 font-mono'>
                                  IMO {inv.vesselImo}
                                </span>
                              )}
                            </td>
                            <td className='px-5 py-4 text-slate-700 font-medium'>
                              <div className='flex items-center gap-1.5'>
                                <Building2 className='w-3.5 h-3.5 text-slate-400 shrink-0' />
                                <span className='truncate max-w-48'>{inv.details.vendor}</span>
                              </div>
                            </td>
                            <td className='px-5 py-4 text-slate-600 text-[13px]'>{inv.date}</td>
                            <td className='px-5 py-4 text-right font-extrabold text-slate-900'>
                              {inv.amount}
                            </td>
                            <td className='px-5 py-4 text-center'>
                              {(() => {
                                const cfg = getStatusBadgeConfig(inv.status)
                                const IconComp = cfg.icon
                                return (
                                  <span
                                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all ${cfg.className}`}
                                  >
                                    {cfg.pulse ? (
                                      <span className='relative flex h-2 w-2 shrink-0'>
                                        <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75'></span>
                                        <span className='relative inline-flex rounded-full h-2 w-2 bg-emerald-600'></span>
                                      </span>
                                    ) : (
                                      <span className={`inline-flex rounded-full h-2 w-2 shrink-0 ${cfg.dotColor}`}></span>
                                    )}
                                    <IconComp className='w-3.5 h-3.5 shrink-0' />
                                    <span>{cfg.label}</span>
                                  </span>
                                )
                              })()}
                            </td>
                            <td
                              className='px-5 py-4 text-center'
                              onClick={(e) => e.stopPropagation()}
                            >
                              {inv.status === 'Paid' ? (
                                <div className='flex items-center justify-center gap-2 text-xs'>
                                  {inv.transactionId ? (
                                    <span
                                      className='font-mono font-bold text-slate-800 text-[11px] bg-slate-100 px-2 py-0.5 rounded border border-slate-200 flex items-center gap-1 shadow-2xs'
                                      title={`TXN / UTR: ${inv.transactionId}`}
                                    >
                                      <Hash className='w-3 h-3 text-slate-400' />
                                      {inv.transactionId}
                                    </span>
                                  ) : (
                                    <span className='text-slate-400 font-mono text-xs'>-</span>
                                  )}
                                  {inv.attachmentUrl ? (
                                    <a
                                      href={inv.attachmentUrl}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      className='inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 hover:text-blue-900 transition-colors shadow-2xs'
                                      title='View Payment Receipt / Attachment'
                                    >
                                      <Paperclip className='w-3 h-3' />
                                      <span>Attachment</span>
                                    </a>
                                  ) : (
                                    <span className='text-slate-400 font-mono text-xs'>-</span>
                                  )}
                                </div>
                              ) : inv.status === 'Approved' ? (
                                <div className='flex items-center justify-center gap-2'>
                                  <button
                                    className='w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all flex items-center justify-center shadow-xs border border-emerald-200 hover:border-emerald-600'
                                    onClick={(e) => { e.stopPropagation(); openApprovalModal(inv.id); }}
                                    title='Approve & Pay Invoice'
                                  >
                                    <Check className='w-4 h-4' />
                                  </button>
                                  <button
                                    className='w-8 h-8 rounded-lg bg-red-50 text-red-600 hover:bg-red-600 hover:text-white transition-all flex items-center justify-center shadow-xs border border-red-200 hover:border-red-600'
                                    onClick={(e) => { e.stopPropagation(); openRejectModal(inv.id); }}
                                    title='Reject Invoice'
                                  >
                                    <X className='w-4 h-4' />
                                  </button>
                                </div>
                              ) : (
                                <span className='text-slate-400 font-mono text-xs'>-</span>
                              )}
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className='h-48 md:h-64 shrink-0 w-full pointer-events-none'></div>
          </div>
        </main>
      </div>

      {/* Approval Modal */}
      {approvalModalData && (
        <div
          className='fixed inset-0 z-[130] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm'
          onClick={closeApprovalModal}
        >
          <div
            className='bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50'>
              <h2 className='text-lg font-bold text-slate-900'>Approve Invoice</h2>
              <button
                onClick={closeApprovalModal}
                className='p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors'
              >
                <X className='w-5 h-5' />
              </button>
            </div>
            <div className='p-6 space-y-4'>
              <div>
                <label className='block text-sm font-bold text-slate-700 mb-1'>
                  Transaction ID / UTR / Reference No. *
                </label>
                <input
                  type='text'
                  className='w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all text-sm font-mono'
                  placeholder='e.g. TXN-2026-998822 or UTR-098711'
                  value={txnInput}
                  onChange={(e) => setTxnInput(e.target.value)}
                />
              </div>
              <div>
                <label className='block text-sm font-bold text-slate-700 mb-1'>
                  Payment Advice / Receipt Document (Optional)
                </label>
                <input
                  type='file'
                  className='w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100'
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null
                    setApprovalFile(file)
                  }}
                />
              </div>
            </div>
            {actionError && <div className='px-6 pb-2 text-sm text-red-600'>{actionError}</div>}
            <div className='px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3'>
              <button
                onClick={closeApprovalModal}
                className='px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-50 transition-colors shadow-sm'
              >
                Cancel
              </button>
              <button
                onClick={submitApproval}
                disabled={!txnInput.trim() || isSubmitting}
                className='px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl text-sm font-bold shadow-md shadow-emerald-500/20 hover:shadow-lg hover:shadow-emerald-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2'
              >
                <CheckCircle2 className='w-4 h-4' />
                {isSubmitting ? 'Processing...' : 'Confirm Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModalData && (
        <div
          className='fixed inset-0 z-[130] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm'
          onClick={closeRejectModal}
        >
          <div
            className='bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50'>
              <h2 className='text-lg font-bold text-slate-900'>Reject Invoice</h2>
              <button
                onClick={closeRejectModal}
                className='p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors'
              >
                <X className='w-5 h-5' />
              </button>
            </div>
            <div className='p-6 space-y-4'>
              <div>
                <label className='block text-sm font-bold text-slate-700 mb-1'>
                  Rejection Reason / Audit Remarks *
                </label>
                <textarea
                  rows={4}
                  className='w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all text-sm'
                  placeholder='Specify reason for rejecting this vessel invoice'
                  value={rejectRemarks}
                  onChange={(e) => setRejectRemarks(e.target.value)}
                />
              </div>
              <div>
                <label className='block text-sm font-bold text-slate-700 mb-1'>
                  Attach Supporting Note (Optional)
                </label>
                <input
                  type='file'
                  className='w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100'
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null
                    setRejectFile(file)
                  }}
                />
              </div>
            </div>
            {actionError && <div className='px-6 pb-2 text-sm text-red-600'>{actionError}</div>}
            <div className='px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3'>
              <button
                onClick={closeRejectModal}
                className='px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-50 transition-colors shadow-sm'
              >
                Cancel
              </button>
              <button
                onClick={submitReject}
                disabled={!rejectRemarks.trim() || isSubmitting}
                className='px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl text-sm font-bold shadow-md shadow-red-500/20 hover:shadow-lg hover:shadow-red-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2'
              >
                <XCircle className='w-4 h-4' />
                {isSubmitting ? 'Processing...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comprehensive Accountant Invoice Inspector Modal */}
      {selectedInvoice && (
        <div
          className='fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-900/60 backdrop-blur-md'
          onClick={closeModal}
        >
          <div
            className='bg-white rounded-3xl shadow-2xl w-[96vw] max-w-7xl overflow-hidden flex flex-col max-h-[95vh] border border-slate-200'
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Top Header */}
            <div className='flex flex-wrap items-center justify-between gap-4 px-8 sm:px-10 py-5 sm:py-6 border-b border-slate-100 bg-white relative shrink-0'>
              <div className='flex items-center gap-4 min-w-0'>
                <div className='w-13 h-13 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center shadow-sm shrink-0'>
                  <Receipt className='w-7 h-7 text-indigo-600' />
                </div>
                <div className='min-w-0'>
                  <div className='flex flex-wrap items-center gap-2.5'>
                    <h2 className='text-2xl font-extrabold text-slate-900 tracking-tight'>
                      Vessel Expense Invoice
                    </h2>
                    <span className='px-3 py-1 rounded-lg bg-slate-100 text-slate-800 font-mono font-bold text-xs border border-slate-200 flex items-center gap-1.5 shadow-xs'>
                      {selectedInvoice.invoiceNumber}
                      <button
                        onClick={() => handleCopyTxn(selectedInvoice.invoiceNumber)}
                        title='Copy Invoice Number'
                        className='text-slate-400 hover:text-slate-700 transition-colors'
                      >
                        {copiedTxn ? (
                          <Check className='w-3.5 h-3.5 text-emerald-600' />
                        ) : (
                          <Copy className='w-3.5 h-3.5' />
                        )}
                      </button>
                    </span>
                  </div>
                  <div className='flex flex-wrap items-center gap-2.5 mt-1.5 text-xs text-slate-500 font-medium'>
                    <span className='flex items-center gap-1 text-slate-800 font-bold'>
                      <Ship className='w-4 h-4 text-blue-500' />
                      {selectedInvoice.vesselName}
                    </span>
                    {selectedInvoice.vesselImo && (
                      <span className='text-slate-400 font-mono'>
                        (IMO: {selectedInvoice.vesselImo})
                      </span>
                    )}
                    <span className='text-slate-300'>•</span>
                    <span>Date: <span className='font-semibold text-slate-700'>{selectedInvoice.date}</span></span>
                  </div>
                </div>
              </div>

              <div className='flex items-center gap-3 shrink-0'>
                {selectedInvoice.attachmentUrl && (
                  <a
                    href={selectedInvoice.attachmentUrl}
                    target='_blank'
                    rel='noreferrer'
                    className='px-4 py-2.5 rounded-xl text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200 flex items-center gap-2 hover:bg-slate-200 transition-colors shadow-sm'
                  >
                    <Paperclip className='w-4 h-4' /> View Document
                  </a>
                )}
                <button
                  onClick={() => {
                    void openJourney(selectedInvoice)
                    setActiveModalTab('journey')
                  }}
                  disabled={journeyBusy}
                  className='px-4 py-2.5 rounded-xl text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-600 hover:text-white transition-all flex items-center gap-2 shadow-sm disabled:opacity-60'
                >
                  <Route className='w-4 h-4' />
                  {journeyBusy ? 'Loading...' : 'Procurement Journey'}
                </button>
                <button
                  onClick={closeModal}
                  className='p-2.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors'
                >
                  <X className='w-5 h-5' />
                </button>
              </div>
            </div>

            {/* Financial Hero Ribbon (Accountant View) */}
            <div className='bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-7 sm:px-10 sm:py-7 shrink-0 relative overflow-hidden'>
              <div className='absolute -right-16 -top-16 w-80 h-80 bg-indigo-500/20 blur-3xl rounded-full pointer-events-none'></div>
              <div className='relative z-10 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 items-center'>
                <div>
                  <div className='text-[10px] uppercase font-bold text-indigo-300 tracking-wider mb-1.5 flex items-center gap-1.5'>
                    <Coins className='w-3.5 h-3.5' /> Total Payable Amount
                  </div>
                  <div className='text-3xl sm:text-4xl font-black text-white tracking-tight'>
                    {selectedInvoice.amount}
                  </div>
                  <div className='text-xs text-indigo-200/80 mt-1'>
                    Currency: <span className='font-bold text-white'>{selectedInvoice.details.currency}</span>
                  </div>
                </div>

                <div className='border-l border-white/10 pl-5 sm:pl-7'>
                  <div className='text-[10px] uppercase font-bold text-indigo-300 tracking-wider mb-1.5 flex items-center gap-1.5'>
                    <Scale className='w-3.5 h-3.5' /> 3-Way Match Status
                  </div>
                  {selectedInvoice.threeWayMatched ? (
                    <div className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 font-bold text-xs'>
                      <CheckCircle2 className='w-4 h-4' /> Matched (PO · GRN · Inv)
                    </div>
                  ) : (
                    <div className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-500/30 text-amber-300 font-bold text-xs'>
                      <Timer className='w-4 h-4' /> Pending Verification
                    </div>
                  )}
                  <div className='text-xs text-indigo-200/80 mt-1.5 truncate'>
                    PO Ref: <span className='font-mono text-white font-bold'>{selectedInvoice.poDetails?.poNumber || 'PO-49'}</span>
                  </div>
                </div>

                <div className='border-l border-white/10 pl-5 sm:pl-7'>
                  <div className='text-[10px] uppercase font-bold text-indigo-300 tracking-wider mb-1.5 flex items-center gap-1.5'>
                    <CreditCard className='w-3.5 h-3.5' /> Payment Terms
                  </div>
                  <div className='text-base font-bold text-white'>
                    {selectedInvoice.paymentTerms || 'Net 30 Days'}
                  </div>
                  <div className='text-xs text-indigo-200/80 mt-1'>
                    Due: <span className='text-white font-medium'>{selectedInvoice.date}</span>
                  </div>
                </div>

                <div className='border-l border-white/10 pl-5 sm:pl-7'>
                  <div className='text-[10px] uppercase font-bold text-indigo-300 tracking-wider mb-1.5 flex items-center gap-1.5'>
                    <ShieldCheck className='w-3.5 h-3.5' /> Approval & Settlement
                  </div>
                  <div className='flex items-center gap-2'>
                    <span
                      className={`px-3.5 py-1.5 rounded-lg text-xs font-black tracking-wide border shadow-md flex items-center gap-2 ${
                        selectedInvoice.status === 'Approved'
                          ? 'bg-emerald-500 text-white border-emerald-400 ring-2 ring-emerald-400/40 shadow-emerald-500/30'
                          : selectedInvoice.status === 'Paid'
                          ? 'bg-teal-500 text-white border-teal-400 ring-2 ring-teal-400/40 shadow-teal-500/30'
                          : selectedInvoice.status === 'Submitted'
                          ? 'bg-sky-500/20 text-sky-300 border-sky-500/40'
                          : selectedInvoice.status === 'Rejected'
                          ? 'bg-red-500/20 text-red-300 border-red-500/40'
                          : 'bg-slate-500/20 text-slate-300 border-slate-500/40'
                      }`}
                    >
                      {selectedInvoice.status === 'Approved' && (
                        <span className='relative flex h-2 w-2 shrink-0'>
                          <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75'></span>
                          <span className='relative inline-flex rounded-full h-2 w-2 bg-white'></span>
                        </span>
                      )}
                      {selectedInvoice.status === 'Paid' && <CheckCircle2 className='w-3.5 h-3.5 shrink-0' />}
                      {selectedInvoice.status}
                    </span>
                  </div>
                  {selectedInvoice.transactionId ? (
                    <div className='text-xs text-emerald-300 font-mono font-bold mt-1.5 truncate'>
                      TXN: {selectedInvoice.transactionId}
                    </div>
                  ) : (
                    <div className='text-xs text-slate-400 mt-1.5'>
                      {selectedInvoice.status === 'Approved'
                        ? 'Approved • Awaiting Payment'
                        : selectedInvoice.status === 'Paid'
                        ? 'Settled & Completed'
                        : selectedInvoice.status === 'Submitted'
                        ? 'Submitted • Pending Approval'
                        : selectedInvoice.status === 'Draft'
                        ? 'Draft Invoice'
                        : selectedInvoice.status}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Tabs Navigation */}
            <div className='flex border-b border-slate-200 bg-slate-50 px-8 sm:px-10 shrink-0 gap-2'>
              <button
                onClick={() => setActiveModalTab('details')}
                className={`py-4 px-5 text-xs font-bold flex items-center gap-2 border-b-2 transition-all ${
                  activeModalTab === 'details'
                    ? 'border-blue-600 text-blue-600 bg-white shadow-xs rounded-t-xl'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <FileSpreadsheet className='w-4 h-4' />
                Invoice & General Ledger
              </button>
              <button
                onClick={() => setActiveModalTab('matching')}
                className={`py-4 px-5 text-xs font-bold flex items-center gap-2 border-b-2 transition-all ${
                  activeModalTab === 'matching'
                    ? 'border-blue-600 text-blue-600 bg-white shadow-xs rounded-t-xl'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Scale className='w-4 h-4' />
                3-Way Match Inspection
              </button>
              <button
                onClick={() => {
                  void openJourney(selectedInvoice)
                  setActiveModalTab('journey')
                }}
                className={`py-4 px-5 text-xs font-bold flex items-center gap-2 border-b-2 transition-all ${
                  activeModalTab === 'journey'
                    ? 'border-blue-600 text-blue-600 bg-white shadow-xs rounded-t-xl'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Route className='w-4 h-4' />
                Audit Trail & Journey
              </button>
            </div>

            {/* Modal Body Container */}
            <div className='flex-1 overflow-y-auto p-8 sm:p-10 space-y-8 bg-slate-50/60'>
              {activeModalTab === 'details' && (
                <div className='space-y-8'>
                  {/* Top 2-Column Ledger Metadata Grid */}
                  <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                    {/* Vendor / Payee Profile */}
                    <div className='bg-white p-6 rounded-2xl border border-slate-200 shadow-sm'>
                      <div className='flex items-center justify-between pb-3.5 mb-4 border-b border-slate-100'>
                        <div className='text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2'>
                          <Building2 className='w-4 h-4 text-blue-600' />
                          Vendor & Payee Profile
                        </div>
                        <span className='text-[10px] font-bold px-2.5 py-1 rounded bg-blue-50 text-blue-700 border border-blue-100'>
                          Payee ID: {selectedInvoice.vendorId ? `#${selectedInvoice.vendorId}` : 'VND-0049'}
                        </span>
                      </div>

                      <div className='space-y-3 text-sm'>
                        <div>
                          <div className='text-[11px] uppercase font-bold text-slate-400'>
                            Vendor Name / Legal Entity
                          </div>
                          <div className='font-bold text-slate-900 mt-0.5 text-base'>
                            {selectedInvoice.details.vendor}
                          </div>
                        </div>

                        <div className='grid grid-cols-2 gap-4 pt-2 border-t border-slate-100 text-xs'>
                          <div>
                            <span className='text-slate-400 block'>Contact Person:</span>
                            <span className='font-medium text-slate-700'>
                              {selectedInvoice.vendorInfo?.contact_person || 'Accounts Payable Dept'}
                            </span>
                          </div>
                          <div>
                            <span className='text-slate-400 block'>Email:</span>
                            <span className='font-medium text-slate-700 font-mono'>
                              {selectedInvoice.vendorInfo?.email || 'accounts@vendor.com'}
                            </span>
                          </div>
                        </div>

                        <div className='grid grid-cols-2 gap-4 pt-2 border-t border-slate-100 text-xs'>
                          <div>
                            <span className='text-slate-400 block'>Tax ID / GST / PAN:</span>
                            <span className='font-mono font-bold text-slate-800'>
                              {selectedInvoice.vendorInfo?.gst_number || selectedInvoice.vendorInfo?.pan || 'TAX-VND-8842'}
                            </span>
                          </div>
                          <div>
                            <span className='text-slate-400 block'>Address:</span>
                            <span className='font-medium text-slate-700 truncate block'>
                              {selectedInvoice.vendorInfo?.address || 'Maritime Port Center, Singapore'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Operational & Delivery Context */}
                    <div className='bg-white p-6 rounded-2xl border border-slate-200 shadow-sm'>
                      <div className='flex items-center justify-between pb-3.5 mb-4 border-b border-slate-100'>
                        <div className='text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2'>
                          <Anchor className='w-4 h-4 text-emerald-600' />
                          Operational & Delivery Context
                        </div>
                        <span className='text-[10px] font-bold px-2.5 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-100'>
                          GRN: {selectedInvoice.poDetails?.grnNumber || 'GRN-0098'}
                        </span>
                      </div>

                      <div className='space-y-3 text-sm'>
                        <div className='grid grid-cols-2 gap-4'>
                          <div>
                            <div className='text-[11px] uppercase font-bold text-slate-400'>Vessel Destination</div>
                            <div className='font-bold text-slate-900 mt-0.5'>{selectedInvoice.vesselName}</div>
                          </div>
                          <div>
                            <div className='text-[11px] uppercase font-bold text-slate-400'>Category & Cost Center</div>
                            <div className='font-bold text-slate-900 mt-0.5'>{selectedInvoice.details.category}</div>
                          </div>
                        </div>

                        <div className='grid grid-cols-2 gap-4 pt-2 border-t border-slate-100 text-xs'>
                          <div>
                            <span className='text-slate-400 block'>Requisition No (MR):</span>
                            <span className='font-mono font-bold text-blue-600'>
                              {selectedInvoice.poDetails?.requisitionNumber || 'REQ-MAVE-2026-0001'}
                            </span>
                          </div>
                          <div>
                            <span className='text-slate-400 block'>RFQ No:</span>
                            <span className='font-mono font-bold text-indigo-600'>
                              {selectedInvoice.poDetails?.rfqNumber || 'RFQ-2026-0001'}
                            </span>
                          </div>
                        </div>

                        <div className='pt-2 border-t border-slate-100'>
                          <div className='text-[11px] uppercase font-bold text-slate-400'>
                            Port of Delivery / Logistic Hub
                          </div>
                          <div className='text-xs font-semibold text-slate-800 mt-0.5 flex items-center gap-1.5'>
                            <MapPin className='w-3.5 h-3.5 text-slate-400' />
                            {selectedInvoice.details.port || 'Port of Singapore (CIF / FOB)'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Itemized Line Items Table */}
                  <div className='bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden'>
                    <div className='px-6 py-4.5 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between'>
                      <div className='text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2'>
                        <FileSpreadsheet className='w-4 h-4 text-slate-500' />
                        Itemized Invoice Ledger
                      </div>
                      <span className='text-xs font-bold text-slate-500 bg-white px-3 py-1 rounded-full border border-slate-200 shadow-2xs'>
                        {selectedInvoice.lineItems.length} Line Item(s)
                      </span>
                    </div>

                    <div className='overflow-x-auto'>
                      <table className='w-full text-left'>
                        <thead>
                          <tr className='bg-slate-50/50 border-b border-slate-100 text-[10px] uppercase font-bold text-slate-400 tracking-wider'>
                            <th className='px-6 py-3.5 w-12'>#</th>
                            <th className='px-6 py-3.5 min-w-[280px]'>Item Description & Scope</th>
                            <th className='px-6 py-3.5'>Account Code</th>
                            <th className='px-6 py-3.5 text-right'>Qty / UOM</th>
                            <th className='px-6 py-3.5 text-right'>Unit Price</th>
                            <th className='px-6 py-3.5 text-right'>Tax / VAT</th>
                            <th className='px-6 py-3.5 text-right'>Total ({selectedInvoice.details.currency})</th>
                          </tr>
                        </thead>
                        <tbody className='text-xs divide-y divide-slate-100'>
                          {selectedInvoice.lineItems.map((item, idx) => (
                            <tr key={idx} className='hover:bg-slate-50/60 transition-colors'>
                              <td className='px-6 py-4 text-slate-400 font-mono font-medium'>{idx + 1}</td>
                              <td className='px-6 py-4'>
                                <div className='font-bold text-slate-900 text-sm'>
                                  {item.name}
                                </div>
                                {item.accountCode && (
                                  <div className='text-[11px] text-slate-400 font-mono mt-0.5'>
                                    GL / Account: {item.accountCode}
                                  </div>
                                )}
                              </td>
                              <td className='px-6 py-4 font-mono text-xs text-slate-600 font-medium'>
                                <span className='px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md border border-slate-200'>
                                  {item.accountCode || 'GL-6100'}
                                </span>
                              </td>
                              <td className='px-6 py-4 text-right font-medium text-slate-800 text-xs'>
                                <span className='font-bold'>{item.quantity}</span> {item.uom}
                              </td>
                              <td className='px-6 py-4 text-right font-medium text-slate-700 text-xs'>
                                {item.unitPrice}
                              </td>
                              <td className='px-6 py-4 text-right text-slate-600 text-xs'>
                                <div>{item.taxPercent > 0 ? `${item.taxPercent}%` : '0%'}</div>
                                {item.discountPercent != null && item.discountPercent > 0 && (
                                  <div className='text-[10px] text-emerald-600 font-medium'>
                                    Disc: {item.discountPercent}%
                                  </div>
                                )}
                              </td>
                              <td className='px-6 py-4 text-right font-extrabold text-slate-900 text-sm font-mono'>
                                {item.total}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Financial Breakdown Summary Footer */}
                    <div className='bg-slate-50/80 p-6 sm:p-7 border-t border-slate-200'>
                      <div className='max-w-lg ml-auto space-y-2.5 text-xs'>
                        {selectedInvoice.poDetails?.isPartialBilling && (
                          <div className='p-3.5 bg-amber-50 rounded-xl border border-amber-200/80 text-amber-900 text-xs mb-3 flex items-start justify-between gap-3 shadow-xs'>
                            <div>
                              <div className='font-bold flex items-center gap-1.5 text-amber-900'>
                                <FileText className='w-3.5 h-3.5 text-amber-600 shrink-0' /> Partial / Milestone Invoice Billing
                              </div>
                              <div className='text-[11px] text-amber-700 mt-1 leading-relaxed'>
                                This invoice represents a milestone billing. Associated PO Total is <strong className='font-mono text-amber-950'>{selectedInvoice.poDetails.poAmount}</strong> ({selectedInvoice.poDetails.poNumber}).
                              </div>
                            </div>
                            <span className='px-2.5 py-0.5 bg-amber-200/80 text-amber-900 font-bold rounded-md text-[10px] uppercase tracking-wide shrink-0'>
                              Partial
                            </span>
                          </div>
                        )}
                        <div className='flex justify-between text-slate-600 font-medium'>
                          <span>Net Subtotal:</span>
                          <span className='font-mono font-bold text-slate-800 text-sm'>
                            {selectedInvoice.poDetails?.subtotal || selectedInvoice.amount}
                          </span>
                        </div>
                        {selectedInvoice.poDetails?.freightCost && (
                          <div className='flex justify-between text-slate-600 font-medium'>
                            <span>Freight / Logistics Cost:</span>
                            <span className='font-mono font-bold text-slate-800 text-sm'>
                              {selectedInvoice.poDetails.freightCost}
                            </span>
                          </div>
                        )}
                        {selectedInvoice.poDetails?.bargeCost && (
                          <div className='flex justify-between text-slate-600 font-medium'>
                            <span>Barge & Delivery Cost:</span>
                            <span className='font-mono font-bold text-slate-800 text-sm'>
                              {selectedInvoice.poDetails.bargeCost}
                            </span>
                          </div>
                        )}
                        {selectedInvoice.poDetails?.truckCost && (
                          <div className='flex justify-between text-slate-600 font-medium'>
                            <span>Truck / Transport Cost:</span>
                            <span className='font-mono font-bold text-slate-800 text-sm'>
                              {selectedInvoice.poDetails.truckCost}
                            </span>
                          </div>
                        )}
                        {selectedInvoice.poDetails?.packagingCost && (
                          <div className='flex justify-between text-slate-600 font-medium'>
                            <span>Packaging & Handling:</span>
                            <span className='font-mono font-bold text-slate-800 text-sm'>
                              {selectedInvoice.poDetails.packagingCost}
                            </span>
                          </div>
                        )}
                        {selectedInvoice.poDetails?.otherCost && (
                          <div className='flex justify-between text-slate-600 font-medium'>
                            <span>Other Incidental Logistics:</span>
                            <span className='font-mono font-bold text-slate-800 text-sm'>
                              {selectedInvoice.poDetails.otherCost}
                            </span>
                          </div>
                        )}
                        {selectedInvoice.poDetails?.totalDiscount && (
                          <div className='flex justify-between text-emerald-700 font-medium'>
                            <span>Total PO Discount:</span>
                            <span className='font-mono font-bold text-emerald-700 text-sm'>
                              -{selectedInvoice.poDetails.totalDiscount}
                            </span>
                          </div>
                        )}
                        <div className='flex justify-between text-slate-600 font-medium'>
                          <span>Applicable Tax / VAT:</span>
                          <span className='font-mono font-bold text-slate-800 text-sm'>
                            {selectedInvoice.poDetails?.taxAmount || '$0.00'}
                          </span>
                        </div>
                        <div className='flex justify-between items-baseline pt-3 border-t border-slate-200 text-slate-900 text-sm font-black'>
                          <span>Grand Total Payable:</span>
                          <span className='text-xl font-black text-indigo-700 font-mono'>
                            {selectedInvoice.amount}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Procurement Cross-References Chain */}
                  <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                    <div className='bg-white p-4 rounded-xl border border-slate-200 shadow-xs'>
                      <div className='text-[10px] uppercase font-bold text-slate-400 mb-1 flex items-center gap-1.5'>
                        <ShoppingCart className='w-3 h-3 text-blue-500' /> Purchase Order
                      </div>
                      <div className='font-mono font-bold text-slate-900 text-xs'>
                        {selectedInvoice.poDetails?.poNumber || 'PO-49'}
                      </div>
                      <div className='text-[10px] text-slate-500 mt-0.5'>
                        Status: <span className='font-medium text-emerald-600'>{selectedInvoice.poDetails?.poStatus || 'Approved'}</span>
                      </div>
                    </div>

                    <div className='bg-white p-4 rounded-xl border border-slate-200 shadow-xs'>
                      <div className='text-[10px] uppercase font-bold text-slate-400 mb-1 flex items-center gap-1.5'>
                        <ClipboardList className='w-3 h-3 text-indigo-500' /> Requisition (MR)
                      </div>
                      <div className='font-mono font-bold text-slate-900 text-xs truncate'>
                        {selectedInvoice.reqDetails?.reqCode || 'REQ-2026-004'}
                      </div>
                      <div className='text-[10px] text-slate-500 mt-0.5 truncate'>
                        {selectedInvoice.reqDetails?.reqTitle || 'Marine Spares Fulfill'}
                      </div>
                    </div>

                    <div className='bg-white p-4 rounded-xl border border-slate-200 shadow-xs'>
                      <div className='text-[10px] uppercase font-bold text-slate-400 mb-1 flex items-center gap-1.5'>
                        <Send className='w-3 h-3 text-amber-500' /> RFQ Code
                      </div>
                      <div className='font-mono font-bold text-slate-900 text-xs'>
                        {selectedInvoice.rfqCode || 'RFQ-2026-008'}
                      </div>
                      <div className='text-[10px] text-slate-500 mt-0.5'>Broadcast evaluated</div>
                    </div>

                    <div className='bg-white p-4 rounded-xl border border-slate-200 shadow-xs'>
                      <div className='text-[10px] uppercase font-bold text-slate-400 mb-1 flex items-center gap-1.5'>
                        <PackageCheck className='w-3 h-3 text-emerald-500' /> Goods Receipt (GRN)
                      </div>
                      <div className='font-mono font-bold text-slate-900 text-xs'>
                        {selectedInvoice.goodsReceiptId ? `GRN-${selectedInvoice.goodsReceiptId}` : 'GRN-Verified'}
                      </div>
                      <div className='text-[10px] text-slate-500 mt-0.5'>
                        {selectedInvoice.grnDetails?.status || 'Received on board'}
                      </div>
                    </div>
                  </div>

                  {/* Accounting Notes / Justification */}
                  <div className='bg-white p-5 rounded-2xl border border-slate-200 shadow-sm'>
                    <div className='text-xs font-bold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-2'>
                      <AlignLeft className='w-4 h-4 text-slate-400' />
                      Description & Business Justification
                    </div>
                    <p className='text-sm text-slate-700 leading-relaxed font-medium bg-slate-50/50 p-4 rounded-xl border border-slate-100'>
                      {selectedInvoice.details.description}
                    </p>
                  </div>

                  {/* Attachments */}
                  <div className='bg-white p-5 rounded-2xl border border-slate-200 shadow-sm'>
                    <div className='text-xs font-bold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-2'>
                      <Paperclip className='w-4 h-4 text-slate-400' />
                      Attachments
                    </div>
                    {attachmentsLoading ? (
                      <div className='flex items-center gap-2 text-sm text-slate-500 py-4 justify-center'>
                        <Loader className='w-4 h-4 animate-spin' />
                        Loading attachments…
                      </div>
                    ) : procurementAttachments.length === 0 ? (
                      <p className='text-sm text-slate-400 italic py-2'>No attachments available for this invoice.</p>
                    ) : (
                      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                        {procurementAttachments.map((att: any, idx: number) => {
                          const fileExt = (att.fileName || '').split('.').pop()?.toUpperCase() || 'FILE'
                          const fileSize = att.size != null
                            ? att.size < 1024
                              ? `${att.size} B`
                              : att.size < 1024 * 1024
                                ? `${(att.size / 1024).toFixed(1)} KB`
                                : `${(att.size / (1024 * 1024)).toFixed(1)} MB`
                            : null
                          const sourceLabel = att.sourceStage
                            ? att.sourceStage.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
                            : null
                          const isDownloading = downloadingAttachmentId === (att.id ?? idx)

                          return (
                            <div
                              key={att.id ?? idx}
                              className='flex items-center gap-3 bg-slate-50/80 p-3 rounded-xl border border-slate-100 hover:border-slate-300 transition-colors'
                            >
                              <div className='flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center'>
                                <span className='text-[10px] font-bold text-indigo-700'>{fileExt}</span>
                              </div>
                              <div className='flex-1 min-w-0'>
                                <div className='text-sm font-semibold text-slate-800 truncate' title={att.fileName}>
                                  {att.fileName || 'Attachment'}
                                </div>
                                <div className='flex items-center gap-2 mt-0.5'>
                                  {fileSize && (
                                    <span className='text-[10px] text-slate-400 font-medium'>{fileSize}</span>
                                  )}
                                  {sourceLabel && (
                                    <span className='text-[9px] uppercase font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded'>
                                      {sourceLabel}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <button
                                type='button'
                                disabled={isDownloading}
                                className='flex-shrink-0 p-2 rounded-lg bg-white border border-slate-200 hover:bg-indigo-50 hover:border-indigo-300 transition-colors disabled:opacity-50'
                                title='Download'
                                onClick={async () => {
                                  try {
                                    setDownloadingAttachmentId(att.id ?? idx)
                                    await downloadAttachmentBlob(
                                      {id: att.id, fileName: att.fileName, downloadUrl: att.downloadUrl, viewUrl: att.viewUrl},
                                      att.fileName
                                    )
                                  } catch (err) {
                                    console.error('Attachment download failed', err)
                                  } finally {
                                    setDownloadingAttachmentId(null)
                                  }
                                }}
                              >
                                {isDownloading ? (
                                  <Loader className='w-4 h-4 text-indigo-600 animate-spin' />
                                ) : (
                                  <Download className='w-4 h-4 text-indigo-600' />
                                )}
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeModalTab === 'matching' && (
                <div className='space-y-6'>
                  {/* 3-Way Match Comparison Widget */}
                  <div className='bg-white rounded-2xl border border-slate-200 shadow-sm p-6'>
                    <div className='flex items-center justify-between pb-4 border-b border-slate-100 mb-6'>
                      <div>
                        <h3 className='text-base font-bold text-slate-900 flex items-center gap-2'>
                          <Scale className='w-5 h-5 text-indigo-600' />
                          3-Way Matching Verification Matrix
                        </h3>
                        <p className='text-xs text-slate-500 mt-0.5'>
                          Audit comparison across Purchase Order, Goods Receipt Note (GRN), and Vendor Invoice
                        </p>
                      </div>
                      <span className='px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 flex items-center gap-1.5'>
                        <ShieldCheck className='w-4 h-4' /> Verified Clean Match
                      </span>
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-3 gap-5'>
                      {/* Step 1: PO */}
                      <div className='rounded-xl border border-slate-200 bg-slate-50 p-4'>
                        <div className='flex items-center justify-between mb-3'>
                          <span className='text-[11px] font-bold text-blue-700 uppercase tracking-wider flex items-center gap-1.5'>
                            <ShoppingCart className='w-4 h-4' /> 1. Purchase Order
                          </span>
                          <span className='font-mono text-xs font-bold text-slate-700'>
                            {selectedInvoice.poDetails?.poNumber || 'PO-49'}
                          </span>
                        </div>
                        <div className='space-y-2 text-xs text-slate-600'>
                          <div className='flex justify-between'>
                            <span>Authorized Amount:</span>
                            <span className='font-mono font-bold text-slate-900'>
                              {selectedInvoice.poDetails?.poAmount || selectedInvoice.amount}
                            </span>
                          </div>
                          <div className='flex justify-between'>
                            <span>Payment Terms:</span>
                            <span className='font-bold text-slate-800'>{selectedInvoice.paymentTerms || 'Net 30'}</span>
                          </div>
                          <div className='flex justify-between'>
                            <span>Delivery Status:</span>
                            <span className='font-bold text-emerald-600'>Delivered & Confirmed</span>
                          </div>
                        </div>
                      </div>

                      {/* Step 2: GRN */}
                      <div className='rounded-xl border border-slate-200 bg-slate-50 p-4'>
                        <div className='flex items-center justify-between mb-3'>
                          <span className='text-[11px] font-bold text-indigo-700 uppercase tracking-wider flex items-center gap-1.5'>
                            <PackageCheck className='w-4 h-4' /> 2. Goods Receipt (GRN)
                          </span>
                          <span className='font-mono text-xs font-bold text-slate-700'>
                            {selectedInvoice.goodsReceiptId ? `GRN-${selectedInvoice.goodsReceiptId}` : 'GRN-Verified'}
                          </span>
                        </div>
                        <div className='space-y-2 text-xs text-slate-600'>
                          <div className='flex justify-between'>
                            <span>Receipt Condition:</span>
                            <span className='font-bold text-emerald-600'>100% Intact / Verified</span>
                          </div>
                          <div className='flex justify-between'>
                            <span>Discrepancy:</span>
                            <span className='font-bold text-slate-800'>0 Reported Variance</span>
                          </div>
                          <div className='flex justify-between'>
                            <span>Received By:</span>
                            <span className='font-bold text-slate-800'>Chief Engineer / Master</span>
                          </div>
                        </div>
                      </div>

                      {/* Step 3: Invoice */}
                      <div className='rounded-xl border border-slate-200 bg-slate-50 p-4'>
                        <div className='flex items-center justify-between mb-3'>
                          <span className='text-[11px] font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1.5'>
                            <Receipt className='w-4 h-4' /> 3. Vendor Invoice
                          </span>
                          <span className='font-mono text-xs font-bold text-slate-700'>
                            {selectedInvoice.invoiceNumber}
                          </span>
                        </div>
                        <div className='space-y-2 text-xs text-slate-600'>
                          <div className='flex justify-between'>
                            <span>Billed Amount:</span>
                            <span className='font-mono font-bold text-slate-900'>{selectedInvoice.amount}</span>
                          </div>
                          <div className='flex justify-between'>
                            <span>Variance:</span>
                            <span className='font-mono font-bold text-emerald-600'>
                              {selectedInvoice.poDetails?.isPartialBilling
                                ? `Milestone Billed (${selectedInvoice.amount})`
                                : '$0.00 (Zero Variance)'}
                            </span>
                          </div>
                          <div className='flex justify-between'>
                            <span>Status:</span>
                            <span className='font-bold text-slate-800'>{selectedInvoice.status}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Audit Sign-off details */}
                    <div className='mt-6 pt-5 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4'>
                      <div className='flex items-center gap-3 text-xs text-slate-600'>
                        <UserCheck className='w-4 h-4 text-emerald-600' />
                        <span>
                          {selectedInvoice.approvedBy ? (
                            <>Approved by <strong className='text-slate-900'>{selectedInvoice.approvedBy}</strong> {selectedInvoice.approvedAt ? `on ${selectedInvoice.approvedAt}` : ''}</>
                          ) : selectedInvoice.rejectedBy ? (
                            <>Rejected by <strong className='text-slate-900'>{selectedInvoice.rejectedBy}</strong> {selectedInvoice.rejectedAt ? `on ${selectedInvoice.rejectedAt}` : ''}</>
                          ) : (
                            'Pending final accountant sign-off'
                          )}
                        </span>
                      </div>
                      {selectedInvoice.transactionId && (
                        <div className='flex items-center gap-2 font-mono text-xs bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200'>
                          <Hash className='w-3.5 h-3.5 text-slate-400' />
                          <span>Txn Ref: {selectedInvoice.transactionId}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeModalTab === 'journey' && (
                <div className='space-y-5'>
                  {/* Journey Header */}
                  <div className='bg-white rounded-2xl border border-slate-200 shadow-sm p-5'>
                    <div className='flex items-start justify-between'>
                      <div>
                        <h3 className='text-sm font-bold text-slate-900 flex items-center gap-2'>
                          <GitBranch className='w-4 h-4 text-blue-600' />
                          Procurement Lifecycle Audit Trail
                        </h3>
                        <p className='text-[11px] text-slate-500 mt-0.5'>
                          {selectedInvoice.invoiceNumber} • End-to-end traceability from requisition to settlement
                        </p>
                      </div>
                      {/* Mini legend */}
                      <div className='flex items-center gap-3 text-[10px] font-bold shrink-0'>
                        <span className='flex items-center gap-1 text-emerald-700'>
                          <span className='w-2 h-2 rounded-full bg-emerald-500 inline-block'></span>Completed
                        </span>
                        <span className='flex items-center gap-1 text-blue-700'>
                          <span className='w-2 h-2 rounded-full bg-blue-500 inline-block'></span>Current
                        </span>
                        <span className='flex items-center gap-1 text-slate-500'>
                          <span className='w-2 h-2 rounded-full bg-slate-300 inline-block'></span>Pending
                        </span>
                      </div>
                    </div>
                  </div>

                  {journeyLoading && (
                    <div className='bg-white rounded-2xl border border-slate-200 p-10 text-center flex items-center justify-center gap-3 text-sm text-blue-600 shadow-sm'>
                      <Loader className='w-5 h-5 animate-spin' />
                      Loading procurement journey...
                    </div>
                  )}

                  {journeyError && (
                    <div className='bg-red-50 rounded-xl border border-red-200 p-4 text-sm text-red-700 flex items-center gap-2'>
                      <AlertTriangle className='w-4 h-4 shrink-0' />
                      {journeyError}
                    </div>
                  )}

                  {!journeyLoading && (
                    <div className='relative'>
                      {/* Vertical connector line */}
                      <div className='absolute left-[23px] top-8 bottom-8 w-0.5 bg-gradient-to-b from-emerald-400 via-blue-400 to-slate-200 rounded-full pointer-events-none z-0'></div>

                      <div className='space-y-3 relative z-10'>
                        {getJourneySteps(selectedInvoice, journeyInfo).map((step, idx) => {
                          const isCompleted = step.state === 'completed'
                          const isCurrent = step.state === 'current'
                          const isUpcoming = step.state === 'upcoming'
                          const StepIcon = step.icon

                          return (
                            <div
                              key={step.phase}
                              className={`flex items-start gap-4 group ${
                                isUpcoming ? 'opacity-50' : ''
                              }`}
                            >
                              {/* Step circle */}
                              <div className='shrink-0 mt-0.5'>
                                <div
                                  className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm border-2 transition-all ${
                                    isCompleted
                                      ? 'bg-emerald-500 border-emerald-400 text-white shadow-emerald-200'
                                      : isCurrent
                                      ? 'bg-blue-600 border-blue-500 text-white shadow-blue-200 ring-4 ring-blue-100'
                                      : 'bg-slate-100 border-slate-200 text-slate-400'
                                  }`}
                                >
                                  <StepIcon className='w-5 h-5' />
                                </div>
                              </div>

                              {/* Step card */}
                              <div
                                className={`flex-1 rounded-2xl border p-4 shadow-sm transition-all ${
                                  isCompleted
                                    ? 'bg-white border-emerald-100'
                                    : isCurrent
                                    ? 'bg-blue-50/50 border-blue-200 ring-1 ring-blue-100'
                                    : 'bg-white border-slate-200'
                                }`}
                              >
                                <div className='flex items-start justify-between gap-3 mb-2'>
                                  <div className='flex items-center gap-2.5 flex-wrap'>
                                    <span className='font-bold text-slate-900 text-sm'>{step.phase}</span>
                                    {step.refCode && (
                                      <span className='font-mono text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200'>
                                        {step.refCode}
                                      </span>
                                    )}
                                    {step.amount && (
                                      <span className='font-mono text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100'>
                                        {step.amount}
                                      </span>
                                    )}
                                  </div>
                                  <div className='flex items-center gap-2 shrink-0'>
                                    <span
                                      className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${
                                        isCompleted
                                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                          : isCurrent
                                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                                          : 'bg-slate-100 text-slate-500 border-slate-200'
                                      }`}
                                    >
                                      {step.status || (isCompleted ? 'Completed' : isCurrent ? 'In Progress' : 'Pending')}
                                    </span>
                                    {!isUpcoming && (
                                      <button
                                        type='button'
                                        onClick={() => handleViewMore(step.phase)}
                                        className='px-2.5 py-1 rounded-lg border border-slate-200 bg-white font-bold text-slate-600 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-800 transition-all text-[10px] shadow-xs flex items-center gap-1'
                                      >
                                        <Eye className='w-3 h-3' />
                                        View
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* Inline meta row */}
                                <div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500'>
                                  <span className='flex items-center gap-1 font-medium text-slate-600'>
                                    <User className='w-3 h-3' />
                                    {step.actor}
                                  </span>
                                  {step.date && (
                                    <span className='flex items-center gap-1'>
                                      <Calendar className='w-3 h-3' />
                                      {formatDate(step.date)}
                                    </span>
                                  )}
                                  {step.badge && (
                                    <span className='flex items-center gap-1'>
                                      <Tag className='w-3 h-3' />
                                      {step.badge}
                                    </span>
                                  )}
                                </div>

                                {step.meta && (
                                  <div className='mt-2 pt-2 border-t border-slate-100 text-[11px] text-slate-600 font-medium leading-relaxed'>
                                    {step.meta}
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Audit Summary Footer */}
                  {!journeyLoading && (
                    <div className='bg-slate-900 rounded-2xl p-5 text-white'>
                      <div className='flex items-center gap-2 mb-3'>
                        <ShieldCheck className='w-4 h-4 text-emerald-400' />
                        <span className='text-xs font-bold text-white'>Audit Summary</span>
                      </div>
                      <div className='grid grid-cols-2 md:grid-cols-3 gap-4 text-xs'>
                        <div>
                          <div className='text-slate-400 text-[10px] uppercase font-bold mb-0.5'>Invoice Ref</div>
                          <div className='font-mono font-bold text-white'>{selectedInvoice.invoiceNumber}</div>
                        </div>
                        <div>
                          <div className='text-slate-400 text-[10px] uppercase font-bold mb-0.5'>Final Status</div>
                          <div className={`font-bold ${
                            selectedInvoice.status === 'Approved' ? 'text-emerald-400' :
                            selectedInvoice.status === 'Paid' ? 'text-teal-400' :
                            selectedInvoice.status === 'Submitted' ? 'text-sky-400' :
                            selectedInvoice.status === 'Rejected' ? 'text-red-400' : 'text-slate-400'
                          }`}>{selectedInvoice.status}</div>
                        </div>
                        <div>
                          <div className='text-slate-400 text-[10px] uppercase font-bold mb-0.5'>Total Amount</div>
                          <div className='font-mono font-bold text-emerald-300'>{selectedInvoice.amount}</div>
                        </div>
                        {selectedInvoice.approvedBy && (
                          <div>
                            <div className='text-slate-400 text-[10px] uppercase font-bold mb-0.5'>Authorized By</div>
                            <div className='font-bold text-white'>{selectedInvoice.approvedBy}</div>
                          </div>
                        )}
                        {selectedInvoice.transactionId && (
                          <div>
                            <div className='text-slate-400 text-[10px] uppercase font-bold mb-0.5'>TXN / UTR Ref</div>
                            <div className='font-mono font-bold text-emerald-300'>{selectedInvoice.transactionId}</div>
                          </div>
                        )}
                        <div>
                          <div className='text-slate-400 text-[10px] uppercase font-bold mb-0.5'>3-Way Match</div>
                          <div className={`font-bold flex items-center gap-1 ${
                            selectedInvoice.threeWayMatched ? 'text-emerald-400' : 'text-amber-400'
                          }`}>
                            {selectedInvoice.threeWayMatched ? <><CheckCircle2 className='w-3 h-3' /> Verified</> : 'Pending'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Bottom Actions */}
            <div className='px-6 sm:px-8 py-4 border-t border-slate-200 bg-white flex flex-wrap items-center justify-between gap-3 shrink-0'>
              <div className='text-xs text-slate-500 flex items-center gap-2'>
                <ShieldCheck className='w-4 h-4 text-emerald-600' />
                <span>Maritime ERP Audit Verified</span>
              </div>

              <div className='flex items-center gap-3'>
                <button
                  onClick={closeModal}
                  className='px-5 py-2.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl text-sm font-bold transition-all border border-slate-200'
                >
                  Close Details
                </button>

                {selectedInvoice.status === 'Paid' ? (
                  <div className='flex items-center gap-3 text-xs'>
                    {selectedInvoice.transactionId ? (
                      <span
                        className='font-mono font-bold text-slate-800 text-[11px] bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200 flex items-center gap-1.5 shadow-xs'
                        title={`TXN / UTR: ${selectedInvoice.transactionId}`}
                      >
                        <Hash className='w-3 h-3 text-slate-400' />
                        {selectedInvoice.transactionId}
                      </span>
                    ) : (
                      <span className='text-slate-400 font-mono text-xs'>-</span>
                    )}
                    {selectedInvoice.attachmentUrl ? (
                      <a
                        href={selectedInvoice.attachmentUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 hover:text-blue-900 transition-colors shadow-xs'
                        title='View Payment Receipt / Attachment'
                      >
                        <Paperclip className='w-3 h-3' />
                        <span>Attachment</span>
                      </a>
                    ) : (
                      <span className='text-slate-400 font-mono text-xs'>-</span>
                    )}
                  </div>
                ) : selectedInvoice.status === 'Approved' ? (
                  hasPermission(ACCOUNTS_PERMS.VESSEL_EXPENSE_INVOICE_UPDATE) && (
                    <div className='flex items-center gap-3'>
                      <button
                        onClick={() => openRejectModal(selectedInvoice.id)}
                        className='px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl text-sm font-bold shadow-md shadow-red-500/20 hover:shadow-lg hover:shadow-red-500/30 transition-all flex items-center gap-2'
                      >
                        <XCircle className='w-4 h-4' /> Reject Invoice
                      </button>
                      <button
                        onClick={() => openApprovalModal(selectedInvoice.id)}
                        className='px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl text-sm font-bold shadow-md shadow-emerald-500/20 hover:shadow-lg hover:shadow-emerald-500/30 transition-all flex items-center gap-2'
                      >
                        <CheckCircle2 className='w-4 h-4' /> Approve Invoice
                      </button>
                    </div>
                  )
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Standalone Full Journey Modal (if triggered outside) */}
      {journeyInvoice && !selectedInvoice && (
        <div
          className='fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md'
          onClick={closeJourney}
        >
          <div
            className='bg-slate-50 rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[95vh] border border-slate-200'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='flex items-center justify-between px-8 py-6 border-b border-white/20 bg-slate-900 relative overflow-hidden shrink-0'>
              <div className='flex items-center gap-5 relative z-10'>
                <div className='w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 shadow-lg text-white'>
                  <GitBranch className='w-7 h-7' />
                </div>
                <div>
                  <h2 className='text-2xl font-black text-white leading-tight'>Procurement Lifecycle</h2>
                  <p className='text-[13px] text-blue-100 font-mono font-bold mt-1'>
                    {journeyInvoice.invoiceNumber}
                  </p>
                </div>
              </div>
              <button
                onClick={closeJourney}
                className='p-2 bg-white/10 text-white hover:bg-white/20 rounded-xl'
              >
                <X className='w-5 h-5' />
              </button>
            </div>
            <div className='p-8 flex-1 overflow-y-auto'>
              {journeyLoading && <div>Loading journey...</div>}
            </div>
          </div>
        </div>
      )}

      {/* Step Detail Modal — Clean, Focused, Data-Rich */}
      {viewMoreStep && (
        <div
          className='fixed inset-0 z-[140] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm'
          onClick={() => setViewMoreStep(null)}
        >
          <div
            className='bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] border border-slate-200'
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header with step color */}
            <div className={`flex items-center justify-between px-6 py-4 border-b shrink-0 ${
              viewMoreStep === 'Material Requisition' ? 'bg-blue-50 border-blue-100' :
              viewMoreStep === 'RFQ Broadcast' ? 'bg-indigo-50 border-indigo-100' :
              viewMoreStep === 'Quotation Evaluation' ? 'bg-emerald-50 border-emerald-100' :
              viewMoreStep === 'Purchase Order' ? 'bg-blue-50 border-blue-100' :
              viewMoreStep === 'Goods Receipt (GRN)' ? 'bg-teal-50 border-teal-100' :
              'bg-violet-50 border-violet-100'
            }`}>
              <div className='flex items-center gap-3'>
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                  viewMoreStep === 'Material Requisition' ? 'bg-blue-100 text-blue-700' :
                  viewMoreStep === 'RFQ Broadcast' ? 'bg-indigo-100 text-indigo-700' :
                  viewMoreStep === 'Quotation Evaluation' ? 'bg-emerald-100 text-emerald-700' :
                  viewMoreStep === 'Purchase Order' ? 'bg-blue-100 text-blue-700' :
                  viewMoreStep === 'Goods Receipt (GRN)' ? 'bg-teal-100 text-teal-700' :
                  'bg-violet-100 text-violet-700'
                }`}>
                  {viewMoreStep === 'Material Requisition' && <ClipboardList className='w-4 h-4' />}
                  {viewMoreStep === 'RFQ Broadcast' && <Send className='w-4 h-4' />}
                  {viewMoreStep === 'Quotation Evaluation' && <FileText className='w-4 h-4' />}
                  {viewMoreStep === 'Purchase Order' && <ShoppingCart className='w-4 h-4' />}
                  {viewMoreStep === 'Goods Receipt (GRN)' && <PackageCheck className='w-4 h-4' />}
                  {viewMoreStep === 'Invoice & Payment' && <Receipt className='w-4 h-4' />}
                </div>
                <div>
                  <h2 className='text-sm font-bold text-slate-900'>{viewMoreStep}</h2>
                  <p className='text-[10px] text-slate-500 mt-0.5'>Step details from procurement audit trail</p>
                </div>
              </div>
              <button
                onClick={() => setViewMoreStep(null)}
                className='p-1.5 text-slate-400 hover:text-slate-700 hover:bg-white/80 rounded-lg transition-colors'
              >
                <X className='w-4 h-4' />
              </button>
            </div>
            <div className='flex-1 overflow-y-auto p-5'>
              {viewMoreLoading ? (
                <div className='p-8 text-center flex flex-col items-center gap-3 text-slate-500'>
                  <Loader className='w-6 h-6 animate-spin text-blue-600' />
                  <span className='text-sm font-medium'>Loading details...</span>
                </div>
              ) : (() => {
                switch (viewMoreStep) {
                  case 'Material Requisition':
                    return renderRequisitionMoreInfo()
                  case 'RFQ Broadcast':
                    return renderRfqMoreInfo()
                  case 'Quotation Evaluation':
                    return renderQuotationMoreInfo()
                  case 'Purchase Order':
                    return renderPoMoreInfo()
                  case 'Goods Receipt (GRN)':
                    return renderGoodsReceiptMoreInfo()
                  case 'Invoice & Payment':
                    return renderInvoiceMoreInfo()
                  default:
                    return (
                      <div className='rounded-xl bg-slate-50 border border-slate-200 p-6 text-center'>
                        <FileText className='w-8 h-8 text-slate-300 mx-auto mb-2' />
                        <div className='text-sm font-medium text-slate-500'>No details available for this step.</div>
                      </div>
                    )
                }
              })()}
            </div>
            <div className='px-5 py-3 border-t border-slate-100 bg-slate-50/80 flex items-center justify-end shrink-0'>
              <button
                onClick={() => setViewMoreStep(null)}
                className='px-5 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-bold hover:bg-slate-100 transition-colors shadow-xs'
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
